/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ReactFlow,
  ReactFlowProvider,
  useNodesState,
  useEdgesState,
  addEdge,
  useReactFlow,
  Controls,
  Background,
  Handle,
  Position,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

import {
  Typography,
  Button,
  Select,
  MenuItem,
  Box,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Paper,
  Divider,
  Autocomplete,
  CircularProgress,
  Card,
  CardContent,
  ToggleButtonGroup,
  ToggleButton,
  IconButton,
  Chip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import "./diagram.css";
import axiosInstance, { fetcher, postFetcher } from "src/utils/axios";
import { enqueueSnackbar } from "notistack";
import { useRouter } from "src/routes/hooks";
import { paths } from "src/routes/paths";
import Iconify from "src/components/iconify";
import AddRawMaterialDialog from "./AddNewRawMaterial";
import AddUtilityDialog from "./AddNewUtitlity";
import AddCostDialog from "./AddNewCost.js";
import AddOutputDialog from "./AddNewOutput";
import AddNewInputFormulaDialog from "./AddInputFormula";
import NodePropertiesDrawer2ndStep from "./NodePropertiesDrawer2ndStep";
import SaveWorkflowDialog from "./SaveWorkflowDialog";
import { useLocation, useParams } from "react-router";
import SmartView from "./SmartView";

const generateCustomId = (prefix = '') => `${prefix}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

// Custom Node Components for React Flow

// ─── Shared styles ────────────────────────────────────────────────────────────
const BLUE = '#3b82f6';
const BLUE_LIGHT = '#eff6ff';
const BLUE_MID = '#dbeafe';

// ─── Regular / Default Node ───────────────────────────────────────────────────
export const CustomNodeComponent = ({ data, selected }) => {
  const inputs = data.input || [];
  const outputs = data.output || [];

  // Collect all product names from inputs
  const productNames = inputs.flatMap(i =>
    (i.product_data || []).map(p => p.name)
  );
  const outputProductNames = outputs.flatMap(o =>
    (o.product_data || []).map(p => p.name)
  );

  const hasMaterial = inputs.some(i => i.is_material);
  const hasUtility = inputs.some(i => i.is_utility);

  return (
    <div style={{
      background: '#fff',
      position: 'relative',
      borderRadius: '14px',
      border: selected ? `2px solid ${BLUE}` : '1.5px solid #d1d9f0',
      boxShadow: selected
        ? '0 0 0 4px rgba(59,130,246,0.12), 0 4px 16px rgba(59,130,246,0.1)'
        : '0 2px 12px rgba(30,40,80,0.08)',
      minWidth: '220px',
      maxWidth: '280px',
      overflow: 'hidden',
      cursor: 'pointer',
      transition: 'box-shadow 0.2s, border-color 0.2s',
    }}>

      {/* Updated / Raw Material badges */}
      {(data?.updated || data?.hasRawMaterial) && (
        <div style={{ position: 'absolute', top: 8, right: 10, display: 'flex', gap: 6 }}>
          {data?.hasRawMaterial && (
            <div style={{
              background: '#f59e0b', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Raw</div>
          )}
          {data?.updated && (
            <div style={{
              background: '#16a34a', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Updated</div>
          )}
        </div>
      )}

      {/* Left connection handle (target - incoming) */}
      <Handle 
        type="target" 
        position={Position.Left} 
        style={{
          background: '#94a3b8', 
          border: '2px solid #fff',
          width: 10, 
          height: 10, 
          left: -6,
        }} 
      />

      {/* Header */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '10px',
        padding: '12px 14px 10px',
        background: BLUE_LIGHT,
        borderBottom: `1px solid ${BLUE_MID}`,
      }}>
        <div style={{
          width: 34, height: 34, borderRadius: '9px',
          background: BLUE, display: 'flex', alignItems: 'center',
          justifyContent: 'center', flexShrink: 0,
        }}>
          {/* Workflow icon */}
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="3" width="7" height="7" rx="1.5" />
            <rect x="14" y="14" width="7" height="7" rx="1.5" />
            <path d="M6.5 10v4h11v-4" />
            <line x1="6.5" y1="14" x2="6.5" y2="17" />
          </svg>
        </div>
        <div>
          <div style={{ fontWeight: 700, fontSize: '13.5px', color: '#1e293b', lineHeight: 1.2 }}>
            {data.label}
          </div>
          <div style={{ fontSize: '11px', color: '#64748b', marginTop: '1px' }}>
            {data.type === 'default' ? 'Process Node' : data.type}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '10px 14px 12px', display: 'flex', flexDirection: 'column', gap: '7px' }}>

        {/* Input / Output counts */}
        <div style={{ display: 'flex', gap: '8px' }}>
          <Badge color="#3b82f6" bg="#eff6ff" label={`${inputs.length} Input${inputs.length !== 1 ? 's' : ''}`} />
          <Badge color="#10b981" bg="#ecfdf5" label={`${outputs.length} Output${outputs.length !== 1 ? 's' : ''}`} />
          {hasMaterial && <Badge color="#f59e0b" bg="#fffbeb" label="Material" />}
          {hasUtility && <Badge color="#8b5cf6" bg="#f5f3ff" label="Utility" />}
        </div>

        {/* Input products */}
        {productNames.length > 0 && (
          <InfoRow icon="📦" label="Inputs" items={productNames} color="#3b82f6" />
        )}

        {/* Output products */}
        {outputProductNames.length > 0 && (
          <InfoRow icon="🏷️" label="Outputs" items={outputProductNames} color="#10b981" />
        )}

        {/* Empty state */}
        {productNames.length === 0 && outputProductNames.length === 0 && (
          <div style={{
            background: '#f8fafc', borderRadius: '8px', padding: '8px 10px',
            color: '#94a3b8', fontSize: '11.5px', textAlign: 'center',
            border: '1px dashed #e2e8f0',
          }}>
            Click to add details…
          </div>
        )}
      </div>

      {/* Right connection handle (source - outgoing) */}
      <Handle 
        type="source" 
        position={Position.Right} 
        style={{
          background: '#94a3b8', 
          border: '2px solid #fff',
          width: 10, 
          height: 10, 
          right: -6,
        }} 
      />
    </div>
  );
};



const PURPLE = '#7c3aed';
const PURPLE_LIGHT = '#f5f3ff';
const PURPLE_MID = '#ede9fe';
const PURPLE_BORDER = '#c4b5fd';
// ─── Diamond / Decision Node ──────────────────────────────────────────────────
export const DiamondNodeComponent = ({ data, selected }) => {

  const inputs = data.input || [];
  const outputs = data.output || []; // array of output IDs or objects
  const accent = PURPLE;

  return (
    <div style={{
      background: '#fff',
      borderRadius: '16px',
      border: selected
        ? `2px solid ${accent}`
        : `1.5px solid ${PURPLE_BORDER}`,
      boxShadow: selected
        ? `0 0 0 4px ${accent}18, 0 4px 20px ${accent}18`
        : '0 2px 12px rgba(100,60,200,0.08)',
      minWidth: '230px',
      maxWidth: '290px',
      fontFamily: "'DM Sans', 'Geist', sans-serif",
      overflow: 'visible', // allow handles to peek outside
      cursor: 'pointer',
      position: 'relative',
      transition: 'box-shadow 0.2s, border-color 0.2s',
    }}>

      {/* Updated / Raw Material badges for diamond nodes */}
      {(data?.updated || data?.hasRawMaterial) && (
        <div style={{ position: 'absolute', top: 10, right: 12, display: 'flex', gap: 6 }}>
          {data?.hasRawMaterial && (
            <div style={{
              background: '#f59e0b', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Raw</div>
          )}
          {data?.updated && (
            <div style={{
              background: '#16a34a', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Updated</div>
          )}
        </div>
      )}

      {/* Top target handle (incoming connection) */}
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: accent,
          border: '2.5px solid #fff',
          width: 12, height: 12,
          top: -7,
          boxShadow: `0 0 0 2px ${accent}44`,
        }}
      />

      {/* ── Header ── */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '11px',
        padding: '13px 15px 11px',
        background: PURPLE_LIGHT,
        borderBottom: `1px solid ${PURPLE_MID}`,
        borderRadius: '14px 14px 0 0',
      }}>
        {/* Icon */}
        <div style={{
          width: 38, height: 38, borderRadius: '11px',
          background: accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 2px 8px ${accent}44`,
        }}>
          {/* Branch / split icon */}
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="6" cy="18" r="2" />
            <circle cx="6" cy="6" r="2" />
            <circle cx="18" cy="12" r="2" />
            <path d="M6 8v8" />
            <path d="M8 18h3a4 4 0 0 0 4-4V8" />
            <path d="M15 6H8" />
          </svg>
        </div>

        {/* Title */}
        <div>
          <div style={{ fontWeight: 700, fontSize: '14px', color: '#1e1b4b', lineHeight: 1.2 }}>
            {data.label || data.name}
          </div>
          <div style={{ fontSize: '11.5px', color: '#7c6faa', marginTop: '2px' }}>
            {inputs.length} input{inputs.length !== 1 ? 's' : ''} · {outputs.length} branch{outputs.length !== 1 ? 'es' : ''}
          </div>
        </div>
      </div>

      {/* ── Output rows ── */}
      <div style={{ padding: '10px 13px 12px', display: 'flex', flexDirection: 'column', gap: '7px' }}>
        {outputs.length === 0 ? (
          <div style={{
            background: '#f8f7ff',
            border: '1.5px dashed #c4b5fd',
            borderRadius: '10px',
            padding: '9px 12px',
            color: '#a78bfa',
            fontSize: '12px',
            textAlign: 'center',
          }}>
            No outputs connected
          </div>
        ) : (
          outputs.map((output, index) => {
            const outputId = typeof output === 'object' ? output.id : output;
            const outputLabel = typeof output === 'object' && output.name
              ? output.name
              : `Output ${index + 1}`;

            return (
              <div key={outputId ?? index} style={{ position: 'relative' }}>
                {/* Output row */}
                <div style={{
                  background: PURPLE_LIGHT,
                  border: `1.5px solid ${PURPLE_MID}`,
                  borderRadius: '10px',
                  padding: '9px 36px 9px 13px', // right padding for handle
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{
                      width: 7, height: 7, borderRadius: '50%',
                      background: accent, flexShrink: 0,
                      opacity: 0.7,
                    }} />
                    <span style={{
                      fontSize: '12.5px', fontWeight: 600,
                      color: '#2e1065',
                    }}>
                      {outputLabel}
                    </span>
                  </div>

                  {outputId && (
                    <span style={{
                      fontSize: '10px', color: '#a78bfa', fontWeight: 500,
                    }}>
                      #{outputId}
                    </span>
                  )}
                </div>

                {/* Per-output source handle on the right */}
                <Handle
                  type="source"
                  position={Position.Right}
                  id={`handle-output-${outputId ?? index}`} // unique handle ID

                  isConnectable={true}
                  data-output-index={index}
                  aria-label={`output-${String(outputId ?? index)}`}
                  style={{
                    background: accent,
                    border: '2.5px solid #fff',
                    width: 12, height: 12,
                    right: -7,
                    top: '50%',
                    transform: 'translateY(-50%)',
                    boxShadow: `0 0 0 2px ${accent}44`,
                  }}
                />
              </div>
            );
          })
        )}
      </div>

      {/* Input count badge (bottom-left) */}
      {inputs.length > 0 && (
        <div style={{
          position: 'absolute', bottom: -11, left: 16,
          background: accent, color: '#fff',
          fontSize: '10px', fontWeight: 700,
          borderRadius: '6px', padding: '2px 8px',
          boxShadow: `0 2px 6px ${accent}44`,
          border: '2px solid #fff',
        }}>
          ↓ {inputs.length} in
        </div>
      )}
    </div>
  );
};
// Add this after DiamondNodeComponent and before Badge component
// ─── Option Node ──────────────────────────────────────────────────
export const OptionNodeComponent = ({ data, selected, id }) => {
  const inputs = data.input || [];
  const outputs = data.output || [];

  // Get options from either options_config or options field
  const optionsConfig = data.options_config || data.options || {};
  const selectedOptionsFromData = data.selected_options || [];
  const options = (optionsConfig.options || []).map(opt => {
    // Check if this option is in selected_options
    const isSelected = selectedOptionsFromData.some(
      selectedOpt => selectedOpt.id === opt.id || selectedOpt.label === opt.label
    );
    return {
      ...opt,
      selected: isSelected || opt.selected || false
    };
  });
  const selectionType = optionsConfig.selectionType || data.selectionType || 'radio';
  const accent = '#f97316'; // Orange accent for option nodes

  // Count selected options if checkbox
  const selectedCount = selectionType === 'checkbox'
    ? options.filter(opt => opt.selected).length
    : 0;

  // Get selected options for saving
  const selectedOptions = options.filter(opt => opt.selected);

  const getCostAmount = (costId, costs) => {
    if (!costId || !costs || costs.length === 0) return 'N/A';

    // Try to find the cost by ID
    const cost = costs.find(c => c.id === costId || c.value === costId);
    if (!cost) return 'N/A';

    // Extract amount from different possible structures
    const amount = cost.amount || cost.value?.amount || cost.value;
    return amount || 'N/A';
  };

  const handleOptionClick = (e, clickedIndex) => {
    // Stop propagation to prevent node selection
    e.stopPropagation();

    if (!data.onOptionSelect) return;

    let updatedOptions;

    if (selectionType === 'radio') {
      // For radio, only one can be selected
      updatedOptions = options.map((opt, idx) => ({
        ...opt,
        selected: idx === clickedIndex
      }));
    } else {
      // For checkbox, toggle selection
      updatedOptions = options.map((opt, idx) =>
        idx === clickedIndex ? { ...opt, selected: !opt.selected } : opt
      );
    }

    // Call the callback with updated options
    data.onOptionSelect(id, updatedOptions);
  };

  return (
    <div style={{
      background: '#fff',
      borderRadius: '16px',
      border: selected
        ? `2px solid ${accent}`
        : `1.5px solid #fed7aa`,
      boxShadow: selected
        ? `0 0 0 4px ${accent}18, 0 4px 20px ${accent}18`
        : '0 2px 12px rgba(249,115,22,0.08)',
      minWidth: '250px',
      maxWidth: '320px',
      fontFamily: "'DM Sans', 'Geist', sans-serif",
      overflow: 'visible',
      cursor: 'pointer',
      transition: 'box-shadow 0.2s, border-color 0.2s',
      position: 'relative',
    }}>

      {/* Updated / Raw Material badges - consistent with other nodes */}
      {(data?.updated || data?.hasRawMaterial) && (
        <div style={{ position: 'absolute', top: 8, right: 10, display: 'flex', gap: 6, zIndex: 10 }}>
          {data?.hasRawMaterial && (
            <div style={{
              background: '#f59e0b', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Raw</div>
          )}
          {data?.updated && (
            <div style={{
              background: '#16a34a', color: '#fff', fontSize: 10, fontWeight: 700,
              padding: '4px 6px', borderRadius: 6, boxShadow: '0 1px 3px rgba(0,0,0,0.12)'
            }}>Updated</div>
          )}
        </div>
      )}

      {/* Left target handle (incoming connection) */}
      <Handle
        type="target"
        position={Position.Left}
        style={{
          background: accent,
          border: '2.5px solid #fff',
          width: 12, height: 12,
          left: -7,
          boxShadow: `0 0 0 2px ${accent}44`,
        }}
      />

      {/* Header - clicking here should select the node */}
      <div
        style={{
          display: 'flex', alignItems: 'center', gap: '11px',
          padding: '13px 15px 11px',
          background: '#fff7ed',
          borderBottom: `1px solid #fed7aa`,
          borderRadius: '14px 14px 0 0',
          cursor: 'pointer',
        }}
      >
        <div style={{
          width: 38, height: 38, borderRadius: '11px',
          background: accent,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          flexShrink: 0,
          boxShadow: `0 2px 8px ${accent}44`,
        }}>
          {/* Choice / options icon */}
          <svg width="19" height="19" viewBox="0 0 24 24" fill="none"
            stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <circle cx="12" cy="12" r="3" fill="#fff" />
            <path d="M7 7l10 10M17 7l-10 10" />
          </svg>
        </div>

        <div>
          <div style={{ fontWeight: 700, fontSize: '14px', color: '#7c2d12', lineHeight: 1.2 }}>
            {data.label || data.name}
          </div>
          <div style={{ fontSize: '11.5px', color: '#9a3412', marginTop: '2px' }}>
            {selectionType === 'radio' ? '🔘 Single choice' : '☑️ Multiple choice'} · {options.length} option{options.length !== 1 ? 's' : ''}
            {selectedCount > 0 && ` (${selectedCount} selected)`}
          </div>
        </div>
      </div>

      {/* Options List */}
      <div style={{ padding: '12px 14px 14px' }}>
        {options.length === 0 ? (
          <div style={{
            background: '#f8f7ff',
            border: '1.5px dashed #fed7aa',
            borderRadius: '10px',
            padding: '12px 12px',
            color: '#f97316',
            fontSize: '12px',
            textAlign: 'center',
          }}>
            No options configured
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {options.map((option, index) => (
              <div
                key={option.id || index}
                onClick={(e) => handleOptionClick(e, index)}
                style={{
                  background: '#fff7ed',
                  border: option.selected
                    ? `2px solid ${accent}`
                    : `1.5px solid #fed7aa`,
                  borderRadius: '8px',
                  padding: '10px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  transition: 'all 0.1s',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: '#ffedd5',
                  }
                }}
              >
                {/* Selection indicator */}
                <div style={{
                  width: selectionType === 'radio' ? '18px' : '16px',
                  height: selectionType === 'radio' ? '18px' : '16px',
                  borderRadius: selectionType === 'radio' ? '50%' : '4px',
                  border: `2px solid ${accent}`,
                  background: option.selected ? accent : 'transparent',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.1s',
                }}>
                  {selectionType === 'checkbox' && option.selected && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </div>

                {/* Option content */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    fontWeight: 600,
                    fontSize: '13px',
                    color: '#7c2d12',
                    marginBottom: '4px',
                  }}>
                    {option.label || `Option ${index + 1}`}
                  </div>

                  {/* Option metadata */}
                  <div style={{
                    display: 'flex',
                    gap: '8px',
                    fontSize: '11px',
                    color: '#b45309',
                    flexWrap: 'wrap',
                  }}>
                    {option.value && (
                      <span>Value: {option.value}</span>
                    )}
                    {option.weight && (
                      <span>Weight: {option.weight}</span>
                    )}
                    {/* Display costs if associated with this option */}
                    {option.costs && option.costs.length > 0 && option.costs.map((cost, idx) => (
                      <Chip
                        key={idx}
                        label={`${cost.name || 'Cost'}: ₹${cost.amount || cost.value?.amount || '0'}`}
                        size="small"
                        sx={{
                          backgroundColor: '#f97316',
                          color: 'white',
                          fontSize: '10px',
                          height: '20px',
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    ))}
                    {/* Backward compatibility for costId */}
                    {option.costId && !option.costs && (
                      <Chip
                        label={`Cost ID: ${option.costId}`}
                        size="small"
                        sx={{
                          backgroundColor: '#f97316',
                          color: 'white',
                          fontSize: '10px',
                          height: '20px',
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Selected options summary */}
        {selectedOptions.length > 0 && (
          <div style={{
            marginTop: '12px',
            paddingTop: '10px',
            borderTop: '1px solid #fed7aa',
          }}>
            <div style={{
              fontSize: '11px',
              fontWeight: 600,
              color: '#b45309',
              marginBottom: '6px',
              textTransform: 'uppercase',
              letterSpacing: '0.5px',
            }}>
              Selected {selectionType === 'radio' ? 'Option' : 'Options'}
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {selectedOptions.map((opt, idx) => (
                <Chip
                  key={idx}
                  label={opt.label}
                  size="small"
                  sx={{
                    backgroundColor: accent,
                    color: 'white',
                    fontSize: '10px',
                    height: '22px',
                  }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Input/Output summary */}
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '12px',
          paddingTop: '10px',
          borderTop: '1px solid #fed7aa',
        }}>
          <Badge color={accent} bg="#fff7ed" label={`${inputs.length} Input${inputs.length !== 1 ? 's' : ''}`} />
          <Badge color="#10b981" bg="#ecfdf5" label={`${outputs.length} Output${outputs.length !== 1 ? 's' : ''}`} />
        </div>
      </div>

      {/* Right source handle (outgoing connection) */}
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: accent,
          border: '2.5px solid #fff',
          width: 12, height: 12,
          right: -7,
          boxShadow: `0 0 0 2px ${accent}44`,
        }}
      />
    </div>
  );
};
// ─── Tiny helper components ───────────────────────────────────────────────────
const Badge = ({ label, color, bg }) => (
  <span style={{
    fontSize: '10px', fontWeight: 600, color, background: bg,
    borderRadius: '5px', padding: '2px 7px', border: `1px solid ${color}33`,
    whiteSpace: 'nowrap',
  }}>{label}</span>
);

const InfoRow = ({ icon, label, items, color }) => (
  <div style={{
    background: '#f8fafc', borderRadius: '8px',
    padding: '7px 10px', border: '1px solid #f1f5f9',
  }}>
    <div style={{
      fontSize: '10px', fontWeight: 700, color: '#94a3b8',
      textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: '4px'
    }}>
      {icon} {label}
    </div>
    {items.map((name, i) => (
      <div key={i} style={{
        fontSize: '11.5px', color: '#334155', fontWeight: 500,
        display: 'flex', alignItems: 'center', gap: '5px',
        marginTop: i > 0 ? '3px' : 0,
      }}>
        <span style={{
          width: 6, height: 6, borderRadius: '50%',
          background: color, flexShrink: 0
        }} />
        {name}
      </div>
    ))}
  </div>
);


const nodeTypes = {
  default: CustomNodeComponent,
  diamond: DiamondNodeComponent,
  custom: CustomNodeComponent,
  option: OptionNodeComponent,
};


// Styled components
const Sidebar = styled(Box)(({ theme }) => ({
  width: 250,
  backgroundColor: theme.palette.grey[100],
  padding: theme.spacing(2),
  borderRight: `1px solid ${theme.palette.divider}`,
  overflowY: "auto",
  display: "flex",
  flexDirection: "column",
  gap: theme.spacing(2),
  justifyContent: 'space-between'
}));

const CanvasContainer = styled(Box)(({ theme }) => ({
  flex: 1,
  position: "relative",
  height: "100vh",
}));

const NodeItem = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  marginBottom: theme.spacing(1),
  display: "flex",
  alignItems: "center",
  gap: theme.spacing(1),
  cursor: "grab",
  "&:hover": {
    backgroundColor: theme.palette.grey[200],
  },
}));

const AddNodeButton = styled(IconButton)(({ theme }) => ({
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "20%",
  height: "20%",
  minWidth: 48,
  minHeight: 48,
  color: theme.palette.text.secondary,
  backgroundColor: theme.palette.background.paper,
  border: `2px dashed ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius * 2,
  transition: theme.transitions.create(
    ["background-color", "border-color", "color", "box-shadow"],
    { duration: theme.transitions.duration.short }
  ),
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    borderStyle: "solid",
    boxShadow: theme.shadows[2],
  },
  "&:active": {
    boxShadow: theme.shadows[4],
  },
  zIndex: 1000,
}));

const ErrorModeBox = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  backgroundColor: theme.palette.error.light,
  border: `2px solid ${theme.palette.error.main}`,
  borderRadius: theme.shape.borderRadius,
}));

// Main Component
function FactoryLayout2ndStepContent() {
 
const location2 = useLocation();
 const router=useRouter();
const queryParams = new URLSearchParams(location2.search);
const primaryid =  queryParams.get('primaryid');
const secondaryid = queryParams.get('secondaryid');
const finalProductId = queryParams.get('final_product_id') || queryParams.get('product_id');
const variantId = queryParams.get('variant_id');
const clientIdFromUrl = queryParams.get('client_id');
const productNameFromUrl = queryParams.get('product_name');
const [resolvedProductName, setResolvedProductName] = useState('');
const [isLoadingWorkflow, setIsLoadingWorkflow] = useState(false);
console.log("Primary ID from URL:", primaryid, "Final Product ID:", finalProductId, "Variant ID:", variantId, "Client ID:", clientIdFromUrl, "Product Name:", productNameFromUrl);
   const ACCENT = '#6366f1';
  const ACCENT_LIGHT = '#eef2ff';
  const { screenToFlowPosition } = useReactFlow();
  const [rfNodes, setRfNodes, onNodesChange] = useNodesState([]);
  const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState([]);
  const reactFlowInstance = useRef(null);
  // Add this with your other useState declarations in FactoryLayout2ndStepContent
  const [savedNonRawNodes, setSavedNonRawNodes] = useState(new Set()); // Track node IDs that have been saved
  const [showDialog, setShowDialog] = useState(false);
  const [nodeName, setNodeName] = useState("");
  const [nodeColor, setNodeColor] = useState("#00c0ff");
  const [nodeType, setNodeType] = useState("default");
  const [nodeInputType, setNodeInputType] = useState("raw");
  const [rawMaterialInputs, setRawMaterialInputs] = useState([]);
  const [previousNodeInputs, setPreviousNodeInputs] = useState([]);
  const [utilityInputs, setUtilityInputs] = useState([]);
  const [nodeCost, setNodeCost] = useState([]);
  const [costOptions, setCostOptions] = useState([]);
  const [availableNodes, setAvailableNodes] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedNode, setSelectedNode] = useState(null);
  const [connectedEdges, setConnectedEdges] = useState([]);
  const [lastAddedNode, setLastAddedNode] = useState(null);
  const [isErrorEdgeMode, setIsErrorEdgeMode] = useState(false);
  const [errorSourceNode, setErrorSourceNode] = useState(null);
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  const saveAllNodesRef = useRef(null);
  const [workflowName, setWorkflowName] = useState("");
  const [savedWorkflows, setSavedWorkflows] = useState([]);
  const [selectedWorkflow, setSelectedWorkflow] = useState("");
  const [editNodeName, setEditNodeName] = useState("");
  const [editRawMaterialInputs, setEditRawMaterialInputs] = useState([]);
  const [editPreviousNodeInputs, setEditPreviousNodeInputs] = useState([]);
  const [editUtilityInputs, setEditUtilityInputs] = useState([]);
  const [editNodeCost, setEditNodeCost] = useState([]);
  const [editNodeInputType, setEditNodeInputType] = useState("raw");
  const [inventoryData, setInventoryData] = useState([]);
  const [utilityData, setUtilityData] = useState([]);
  const [nodeListVersion, setNodeListVersion] = useState(0);
  const [showRawMaterialDialog, setShowRawMaterialDialog] = useState(false);
  const [showOutputDialog, setShowOutputDialog] = useState(false);
  const [showUtilityDialog, setShowUtilityDialog] = useState(false);
  const [showCostDialog, setShowCostDialog] = useState(false);
  const [editEdgeLabel, setEditEdgeLabel] = useState("");
  const [selectedEdgeId, setSelectedEdgeId] = useState(null);
  const [outputids, setOutputids] = useState([]);
  const [editoutputids, seteditoutputids] = useState([])
  const [fetchedOutputs, setFetchedOutputs] = useState([]);
  const [nodes, setNodes] = useState([]);
  const [inputname, setInputname] = useState('');
  const [allocatedoutputs, setAllocatedoutputs] = useState([]);
  const [showInputDialog, setshowInputDialog] = useState(false)
  const [prefilledOutput, setPrefilledOutput] = useState(0);
  const [inputFormula, setinputFormula] = useState([])
  const [isWorkflowLoaded, setIsWorkflowLoaded] = useState(false);
  const [selectedworkflowId, setselectedworkflowId] = useState(null)
  const [showOutputSelectionDialog, setShowOutputSelectionDialog] = useState(false);
  const [sourceNodeForEdge, setSourceNodeForEdge] = useState(null);
  const [targetNodeForEdge, setTargetNodeForEdge] = useState(null);
  const [edgeOutputs, setEdgeOutputs] = useState([]);
  const [selectedOutput, setSelectedOutput] = useState(null);
  const [edgeCreationType, setEdgeCreationType] = useState(null);
  const [nodeData, setNodeData] = useState(null);
  const [loadingNodeData, setLoadingNodeData] = useState(false);
  const [nodeDataCache, setNodeDataCache] = useState(new Map());
  const [nodefromworkflow, setnodefromworkflow] = useState([])
  const [utitilityfromdrawer, setutitilityfromdrawer] = useState([])
  const [issaveworkflowready, setissaveworkflowready] = useState(false)
  const [outputsavedforedges, setoutputsavedforedges] = useState([])
  const [clickednodes, setclickednodes] = useState([]);
  const [allVariantIds, setAllVariantIds] = useState([]);
  const [isSavingNodes, setIsSavingingNodes] = useState(false);
  const [isEditing, setIsEditing] = useState(false)
  const [selectedVariantsfromparent, setselectedVariantsfromparent] = useState([])
  const [clientData, setClientData] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [combinedVariantIds, setCombinedVariantIds] = useState([]);
  // Add this with your other useState declarations
  const [nodeSelectedOptions, setNodeSelectedOptions] = useState({}); // Key: nodeId, Value: selected options array
  // keep combinedVariantIds in sync with the global list that the drawer maintains.
const [smartViewOpen, setSmartViewOpen] = useState(false);
const [finalQuantity, setFinalQuantity] = useState(null);
const [availableVariants, setAvailableVariants] = useState([]);
const [selectedVariants, setSelectedVariants] = useState([]);
const [showVariantDialog, setShowVariantDialog] = useState(false);
const [variantoptions, setvariantoptions] = useState([]);
const [selectedworkflowvariant, setselectedworkflowvariant] = useState('');
const [selectedmasterworkflow, setselectedmasterworkflow] = useState(null);

  // Fetch workflow variants when selectedWorkflow changes
  const handlefetchworkflowvariants = useCallback(async (id) => {
    if (!id) return;
    try {
      const response = await fetcher([`/new/workflow-app/process/in/workflow/?process_creation_id=${id}`]);
      if (Array.isArray(response) && response.length > 0) {
        setvariantoptions(response);
        setselectedworkflowvariant(prev => {
          const exists = response.some(v => v.name === prev);
          return exists ? prev : (response[response.length - 1]?.name || response[0]?.name || '');
        });
      } else {
        setvariantoptions([]);
        setselectedworkflowvariant('');
      }
    } catch (err) {
      console.error('Error fetching workflow variants:', err);
    }
  }, []);

  useEffect(() => {
    if (selectedWorkflow && savedWorkflows.length > 0) {
      const workflow = savedWorkflows.find((w) => w.name === selectedWorkflow);
      if (workflow) {
        handlefetchworkflowvariants(workflow.id);
        setselectedmasterworkflow(workflow.id);
      }
    }
  }, [selectedWorkflow, savedWorkflows, handlefetchworkflowvariants]);

  useEffect(() => {
    setCombinedVariantIds(allVariantIds);
  }, [allVariantIds]);
  const isErrorEdgeModeRef = useRef(false);
  const isWorkflowLoadedRef = useRef(isWorkflowLoaded);
  const errorSourceNodeRef = useRef(null);
  const handleNodeSelectionRef = useRef();
  const nodefromworkflowRef = useRef(nodefromworkflow);
  const route = useRouter();
  const location = useLocation();
const handleSmartViewNodeUpdate = useCallback((node, updatedData) => {
  const oldId = node.id;
  const newBackendId = updatedData?.backendId;
  const newId = newBackendId ? newBackendId.toString() : oldId;

  setRfNodes(prevNodes => 
    prevNodes.map(n => {
      if (n.id === oldId || (n.data?.backendId && newBackendId && String(n.data.backendId) === String(newBackendId))) {
        return {
          ...n,
          id: newId,
          data: {
            ...n.data,
            ...updatedData,
            backendId: newBackendId || n.data.backendId,
            id: newId,
          }
        };
      }
      return n;
    })
  );

  setNodes(prevNodes =>
    prevNodes.map(n => {
      if (n.id === oldId || (n.backendId && newBackendId && String(n.backendId) === String(newBackendId))) {
        return {
          ...n,
          backendId: newBackendId || n.backendId,
          id: newId,
          name: updatedData.label || updatedData.name || n.name,
        };
      }
      return n;
    })
  );

  if (oldId !== newId) {
    setRfEdges(prevEdges =>
      prevEdges.map(e => ({
        ...e,
        source: e.source === oldId ? newId : e.source,
        target: e.target === oldId ? newId : e.target,
      }))
    );
  }
}, [setRfNodes, setNodes, setRfEdges]);
  useEffect(() => {
    isErrorEdgeModeRef.current = isErrorEdgeMode;
    errorSourceNodeRef.current = errorSourceNode;
  }, [isErrorEdgeMode, errorSourceNode]);

  useEffect(() => {
    isWorkflowLoadedRef.current = isWorkflowLoaded;
  }, [isWorkflowLoaded]);

  useEffect(() => {
    nodefromworkflowRef.current = nodefromworkflow;
  }, [nodefromworkflow, isWorkflowLoaded]);

  useEffect(() => {
    // Check if any clicked node is inside nodes
    const hasMatch = nodes.some(node => clickednodes.includes(node.backendId));
    setissaveworkflowready(hasMatch);
  }, [nodes, clickednodes]);

  useEffect(() => {
    const backendId = selectedNode?.data?.backendId;
    if (clickednodes.includes(backendId)) {
      setIsEditing(true);
    } else {
      setIsEditing(false);
    }
  }, [selectedNode, clickednodes]);

  // Fetch workflows from API on component mount
  useEffect(() => {
    const preventNodeDeletion = (e) => {
      if (e.key === "Backspace" || e.key === "Delete") {
        const activeEl = document.activeElement;
        const isInInput =
          activeEl.tagName === "INPUT" ||
          activeEl.tagName === "TEXTAREA" ||
          activeEl.isContentEditable ||
          activeEl.closest('[contenteditable="true"]') ||
          activeEl.closest('.MuiDialog-content') ||
          activeEl.closest('.MuiAutocomplete-input') ||
          activeEl.closest('.ql-editor') ||
          activeEl.closest('[role="textbox"]') ||
          activeEl.closest('form');

        if (isInInput || document.querySelector('.MuiDialog-root')) {
          e.stopPropagation();
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        enqueueSnackbar("Node deletion disabled.", {
          variant: "info",
          preventDuplicate: true,
        });
      }
    };

    document.addEventListener("keydown", preventNodeDeletion, true);

    const canvasContainer = document.querySelector(".react-flow");
    if (canvasContainer) {
      canvasContainer.addEventListener("keydown", preventNodeDeletion, true);
    }

    return () => {
      document.removeEventListener("keydown", preventNodeDeletion, true);
      if (canvasContainer) {
        canvasContainer.removeEventListener("keydown", preventNodeDeletion, true);
      }
    };
  }, []);
  // Add this function to handle option selections
  const handleOptionSelect = useCallback((nodeId, updatedOptions) => {
    setNodeSelectedOptions(prev => ({
      ...prev,
      [nodeId]: updatedOptions.filter(opt => opt.selected)
    }));

    setRfNodes(prevNodes => {
      const nodeToUpdate = prevNodes.find(n => n.id === nodeId);
      if (!nodeToUpdate) return prevNodes;

      const newOptions = {
        ...(nodeToUpdate.data.options || nodeToUpdate.data.options_config || {}),
        options: updatedOptions
      };

      const updatedNode = {
        ...nodeToUpdate,
        data: {
          ...nodeToUpdate.data,
          options: newOptions,
          options_config: newOptions,
          selected_options: updatedOptions.filter(opt => opt.selected)
        }
      };

      setSelectedNode(prevSelectedNode => {
        if (prevSelectedNode && prevSelectedNode.id === nodeId) {
          return updatedNode;
        }
        return prevSelectedNode;
      });

      const nodeBackendId = updatedNode.data.backendId;
      if (nodeBackendId) {
        setNodes(prevNodesInternal =>
          prevNodesInternal.map(node => {
            if (node.backendId === nodeBackendId) {
              return {
                ...node,
                selected_options: updatedOptions.filter(opt => opt.selected)
              };
            }
            return node;
          })
        );
      }

      return prevNodes.map(n => (n.id === nodeId ? updatedNode : n));
    });
  }, [setNodeSelectedOptions, setRfNodes, setSelectedNode, setNodes]);
  useEffect(() => {
    const fetchWorkflows = async () => {
      try {
        const response = await postFetcher(['/new/workflow-app/all/processcreation/show/']);
        if (!response) {
          throw new Error('Failed to fetch workflows');
        }
        setSavedWorkflows(response);
      } catch (error) {
        enqueueSnackbar('Failed to load workflows from API', { variant: 'error' });
      }
    };

    fetchWorkflows();
  }, []);

  // useEffect(() => {
  //   if (prefilledOutput && edgeOutputs && showOutputSelectionDialog && previousNodeInputs.length > 0) {
  //     const related_obj = edgeOutputs.find((edgeOutput) => edgeOutput.output_id === prefilledOutput.outputId);
  //     setSelectedOutput(related_obj);

  //     let nodeState = null;

  //     if (isWorkflowLoaded) {
  //       const nodeLayer = nodefromworkflow?.model?.layers?.find(layer => layer.type === 'diagram-nodes');
  //       const nodesFromWorkflow = nodeLayer ? Object.values(nodeLayer.models) : [];
  //       const backendId = previousNodeInputs[0]?.backendId?.toString();

  //       if (!backendId) {
  //         setSourceNodeForEdge(null);
  //         setEdgeOutputs([]);
  //         setShowOutputSelectionDialog(false);
  //         return;
  //       }

  //       const nodeData1 = nodesFromWorkflow.find(n => n.backend_id?.toString() === backendId);
  //       if (nodeData1) {
  //         nodeState = rfNodes.find(n => n.data.backendId?.toString() === backendId);
  //       } else {
  //         console.warn('[useEffect] No node found in nodefromworkflow for backendId:', backendId);
  //       }
  //     } else {
  //       const backendId = previousNodeInputs[0]?.backendId?.toString();
  //       if (!backendId) {
  //         setSourceNodeForEdge(null);
  //         setEdgeOutputs([]);
  //         setShowOutputSelectionDialog(false);
  //         return;
  //       }
  //       nodeState = rfNodes.find(n => n.data.backendId?.toString() === backendId);
  //     }

  //     if (nodeState) {
  //       setSourceNodeForEdge(nodeState);
  //       fetchEdgeOutputs(nodeState);
  //     } else {
  //       setSourceNodeForEdge(null);
  //       setEdgeOutputs([]);
  //       setShowOutputSelectionDialog(false);
  //     }
  //   }
  // }, [prefilledOutput, showOutputSelectionDialog, previousNodeInputs, nodefromworkflow, rfNodes, isWorkflowLoaded]);

  const fetchInputInventory = async () => {
    try {
      const res = await fetcher(['/new/workflow-app/product/list/']);
      setInventoryData(res);
    } catch (error) {
      console.error('Error fetching inventory:', error);
    }
  };

  const fetchUtilityList = async () => {
    try {
      const res = await fetcher(['/new/workflow-app/utility/list/']);
      setUtilityData(res);
    } catch (error) {
      console.error('Error fetching utility list:', error);
    }
  };

  const fetchCostList = async () => {
    try {
      const res = await fetcher(['/new/workflow-app/costs/']);
      const formattedCosts = res.map(item => ({
        label: `${item.name} (${item.value.amount} ${item.value.unit} - ${item.value.type})`,
        value: item.id.toString(),
        ...item
      }));
      setCostOptions(formattedCosts);
    } catch (error) {
      console.error('Error fetching cost list:', error);
    }
  };

  const fetchOutputData = async () => {
    try {
      const savedOutputIds = JSON.parse(sessionStorage.getItem('savedOutputIds') || '[]');
      setOutputids(savedOutputIds);
      if (savedOutputIds.length === 0) {
        setFetchedOutputs([]);
        return;
      }
      const payload = { output_ids: savedOutputIds };
      const res = await postFetcher(["/new/workflow-app/output/list/", payload]);
      if (res) {
        const outputs = Array.isArray(res) ? res : res.data?.outputs || [];
        setFetchedOutputs(outputs);
      } else {
        setFetchedOutputs([]);
      }
    } catch (error) {
      console.error('Error fetching output data:', error);
      setFetchedOutputs([]);
    }
  };

  const fetchEdgeOutputs = useCallback(async (sourceNode) => {
    if (!sourceNode || !sourceNode.data) {
      setEdgeOutputs([]);
      return;
    }

    try {
      const sourceNodeBackendId = sourceNode.data.backendId?.toString();
      let sourceNodeData;

      await new Promise(resolve => setTimeout(resolve, 50));

      if (isWorkflowLoadedRef.current) {
        const nodeLayer = nodefromworkflowRef.current?.model?.layers?.find(
          layer => layer.type === "diagram-nodes"
        );
        const nodesFromWorkflow = nodeLayer ? Object.values(nodeLayer.models) : [];
        sourceNodeData = nodesFromWorkflow.find(
          n => n.backend_id?.toString() === sourceNodeBackendId
        );
      } else {
        sourceNodeData = nodes.find(
          n =>
            n.id?.toString() === sourceNodeBackendId ||
            n.backendId?.toString() === sourceNodeBackendId
        );
      }

      if (!sourceNodeData || !sourceNodeData.output_foreign || sourceNodeData.output_foreign.length === 0) {
        setEdgeOutputs([]);
        return;
      }

      const availableOutputIds = sourceNodeData.output_foreign.filter(
        id => !allocatedoutputs.includes(id.toString())
      );

      if (availableOutputIds.length === 0) {
        setEdgeOutputs([]);
        return;
      }

      let idsarray;
      if (typeof availableOutputIds[0] === 'object' && availableOutputIds[0] !== null) {
        idsarray = availableOutputIds.map(obj => obj.id);
      } else {
        idsarray = availableOutputIds;
      }

      const payload = { output_ids: idsarray };
      const res = await postFetcher(["/new/workflow-app/output/list/", payload]);
      const outputs = Array.isArray(res) ? res : res || [];

      const mappedOutputs = outputs.map(output => ({
        label: output.output_name,
        value: output.output_id.toString(),
        ...output
      }));

      setEdgeOutputs(mappedOutputs);
      setoutputsavedforedges((prev) => [...prev, mappedOutputs]);
    } catch (error) {
      setEdgeOutputs([]);
    }
  }, [nodes, allocatedoutputs, nodefromworkflow]);

  useEffect(() => {
    if (!showOutputDialog) {
      fetchOutputData();
    }
  }, [showOutputDialog]);

  useEffect(() => {
    fetchInputInventory();
    fetchUtilityList();
    fetchCostList();
  }, []);

  useEffect(() => {
    if (showDialog) {
      fetchOutputData();
    }
  }, [showDialog]);

  const inventoryOptions = inventoryData.map(item => ({
    label: item.name,
    value: item.id.toString(),
    product: { id: item.id, name: item.name }
  }));

  const utilityOptions = utilityData.map(item => ({
    label: `${item.name} (Value: ${item.value?.quantity})`,
    value: item.id.toString(),
    ...item,
  }));

  const sourceNodeOptions = useMemo(() => {
    return rfNodes
      .filter(node => {
        const isDifferentNode = !drawerOpen || !selectedNode || node.id !== selectedNode.id;
        return isDifferentNode;
      })
      .map(node => ({
        label: node.data.label || "Unnamed Node",
        id: node.id,
        backendId: node.data.backendId
      }));
  }, [rfNodes, selectedNode, drawerOpen]);

  const nodeHasRawMaterials = async (node) => {
    if (!node || !node.data) return false;

    const backendId = node.data.backendId;
    if (!backendId) return false;

    try {
      const res = await fetcher([`/new/workflow-app/node/${backendId}/`]);
      const inputForeign = Array.isArray(res?.input_foreign) ? res.input_foreign : [];

      // Check all input_foreign entries for product_ids
      return inputForeign.some(item => Array.isArray(item?.value?.product_ids) && item.value.product_ids.length > 0);
    } catch (error) {
      return false;
    }
  };

  const handleRemoveEdge = (edgeId) => {
    const edge = rfEdges.find(e => e.id === edgeId);

    if (edge) {
      const outputIdToFree = edge.data?.outputId;
      if (outputIdToFree) {
        setAllocatedoutputs(prev => prev.filter(id => id !== outputIdToFree));
      }

      setRfEdges(eds => eds.filter(e => e.id !== edgeId));

      if (selectedNode) {
        const remainingEdges = rfEdges.filter(e => e.id !== edgeId && (e.source === selectedNode.id || e.target === selectedNode.id));
        const edgeDetails = remainingEdges.map(e => {
          const sourceNode = rfNodes.find(n => n.id === e.source);
          const targetNode = rfNodes.find(n => n.id === e.target);

          return {
            id: e.id,
            sourceNode: sourceNode?.data.label || 'Unknown',
            sourcePort: 'Right',
            targetNode: targetNode?.data.label || 'Unknown',
            targetPort: 'Left',
            isErrorEdge: e.style?.stroke === 'red' || false,
            label: e.label || '',
            outputId: e.data?.outputId
          };
        });
        setConnectedEdges(edgeDetails);
      } else {
        setConnectedEdges([]);
      }

      setSelectedEdgeId(null);
      setEditEdgeLabel("");
      enqueueSnackbar(`Edge removed successfully!`, { variant: 'success' });
    } else {
      enqueueSnackbar('Failed to remove edge!', { variant: 'error' });
    }
  };

  const handleoutputdialog = () => {
    setShowOutputDialog(true);
    sessionStorage.removeItem('savedOutputIds');
  };

  const handleinputformuladialog = () => {
    setshowInputDialog(true);
  };

  const handleAddOrEditEdgeLabel = (edgeId) => {
    if (!editEdgeLabel.trim()) {
      enqueueSnackbar('Please enter a valid label', { variant: 'error' });
      return;
    }

    setRfEdges(eds => eds.map(e => {
      if (e.id === edgeId) {
        return { ...e, label: editEdgeLabel };
      }
      return e;
    }));

    setConnectedEdges(prev => prev.map(edge =>
      edge.id === edgeId ? { ...edge, label: editEdgeLabel } : edge
    ));

    enqueueSnackbar(`Label "${editEdgeLabel}" updated for edge`, { variant: 'success' });
    setEditEdgeLabel("");
    setSelectedEdgeId(null);
  };

  const saveWorkflow = async () => {
    if (!workflowName.trim()) {
      enqueueSnackbar('Please enter a workflow name', { variant: 'error' });
      return;
    }

    let freshNodeUpdates = {};
    if (saveAllNodesRef.current) {
      try {
        freshNodeUpdates = await saveAllNodesRef.current();
      } catch (e) {
        console.error('Error executing saveAllNodesRef:', e);
      }
    }

    const workflow = savedWorkflows.find((w) => w.name === selectedWorkflow);

    // Create a mapping from old frontend IDs to new backend IDs
    const idMap = {};
    rfNodes.forEach(node => {
      const freshBackendId = freshNodeUpdates?.[node.id] || freshNodeUpdates?.[node.data?.backendId] || node.data?.backendId;
      if (freshBackendId) {
        idMap[node.id] = freshBackendId.toString();
        if (node.data?.backendId) {
          idMap[node.data.backendId.toString()] = freshBackendId.toString();
        }
      }
    });

    const modelToSave = {
      nodes: rfNodes.map(node => {
        const { onOptionSelect, ...data } = node.data;
        const freshBackendId = freshNodeUpdates?.[node.id] || freshNodeUpdates?.[data.backendId] || data.backendId;
        const freshIdStr = freshBackendId ? freshBackendId.toString() : (data.id || node.id);

        if (node.type === 'option' && (!data.selected_options || data.selected_options.length === 0)) {
          delete data.selected_options;
        }

        return {
          id: freshIdStr,
          type: node.type,
          position: node.position,
          data: {
            ...data,
            backendId: freshBackendId || data.backendId,
            id: freshIdStr,
          },
        };
      }),
      edges: rfEdges.map(edge => {
        // Get the new source and target IDs from the mapping
        const newSourceId = idMap[edge.source] || edge.source;
        const newTargetId = idMap[edge.target] || edge.target;

        // Generate new edge ID using source-target format
        const newEdgeId = `e-${newSourceId}-${newTargetId}-${Date.now()}`;

        return {
          id: newEdgeId, // Use the new generated ID
          source: newSourceId,
          target: newTargetId,
          label: edge.label,
          style: edge.style,
          data: edge.data
        };
      }),
      viewport: { x: 0, y: 0, zoom: 1 }
    };

    const updatedAvailableNodes = nodes.map(node => {
      const freshBackendId = freshNodeUpdates?.[node.id] || freshNodeUpdates?.[node.backendId] || node.backendId;
      const freshIdStr = freshBackendId ? freshBackendId.toString() : node.id;
      return {
        backendId: freshBackendId,
        id: freshIdStr,
        name: node.name,
        color: node.color || '#00c0ff',
        type: node.type,
        input: node.input_foreign || [],
        output: node.output_foreign || [],
        cost: node.cost_foreign || [],
      };
    });

    const newWorkflow = {
      name: workflowName,
      process_creation_id: selectedworkflowId || workflow?.id || primaryid || null,
      model: modelToSave,
      availableNodes: updatedAvailableNodes,
      allocated_outputs: allocatedoutputs,
      client_id: selectedClient?.id || null,
      final_quantity: finalQuantity
    };

    try {
      let res;
      let existingVariantId = null;

      if (nodefromworkflow && (nodefromworkflow.id || nodefromworkflow.backendId)) {
        existingVariantId = nodefromworkflow.id || nodefromworkflow.backendId;
      } else if (selectedworkflowvariant && variantoptions.length > 0) {
        const matchingVar = variantoptions.find(v => v.name === selectedworkflowvariant || v.name === workflowName);
        if (matchingVar) {
          existingVariantId = matchingVar.id;
        }
      }

      if (existingVariantId) {
        try {
          res = await patchFetcher([
            `${process.env.REACT_APP_HOST_API}/new/workflow-app/react-flow/workflowcreation/update/?workflow_creation_id=${existingVariantId}`,
            newWorkflow
          ]);
        } catch (updateErr) {
          console.log('PATCH workflowcreation update failed, falling back to POST save:', updateErr);
          res = await postFetcher([
            `${process.env.REACT_APP_HOST_API}/new/workflow-app/react-flow/workflowcreation/save/`,
            newWorkflow,
          ]);
        }
      } else {
        res = await postFetcher([
          `${process.env.REACT_APP_HOST_API}/new/workflow-app/react-flow/workflowcreation/save/`,
          newWorkflow,
        ]);
      }

      if (res?.workflow_id || res?.id) {
        const savedId = res.workflow_id || res.id;
        setnodefromworkflow(prev => ({ ...prev, ...res, id: savedId }));
      }

      setWorkflowName("");
      setShowSaveDialog(false);
      enqueueSnackbar(`Workflow "${workflowName}" saved/updated successfully!`, { variant: 'success' });
      router.push(paths.dashboard.finalproduct.root);
    } catch (error) {
      enqueueSnackbar('Error saving workflow!', { variant: 'error' });
      console.error(error);
    }
  };

  const fetchWorkflow = async (workflowId) => {
    try {
      // 1. Try workflowcreation (workflow variant) endpoint first
      try {
        const response = await fetch(
          `${process.env.REACT_APP_HOST_API}/new/workflow-app/react-flow/workflowcreation/show/?workflow_creation_id=${workflowId}`,
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
            },
          }
        );
        if (response.ok) {
          const data = await response.json();
          if (data && (data.model || data.nodes || data.id || data.process_creation_id)) {
            return data;
          }
        }
      } catch (e) {
        console.log('workflowcreation/show fetch failed, trying processcreation/show:', e);
      }

      // 2. Fallback to processcreation (master process) endpoint
      const response = await fetch(
        `${process.env.REACT_APP_HOST_API}/new/workflow-app/react-flow/processcreation/show/?process_creation_id=${workflowId}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      if (!response.ok) {
        throw new Error('Failed to fetch workflow');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching workflow:', error);
      throw error;
    }
  };

const fetchVariantsForProducts = async (productIds, targetVariantIds = null) => {
  try {
    const idsToFetch = Array.isArray(productIds) ? productIds.join(',') : productIds;
    const response = await fetcher(`/new/workflow-app/products/variants/list/${idsToFetch}/`);
    
    if (response && Array.isArray(response)) {
      // Transform the response into a format suitable for dropdown
      const variantsData = response.map(variant => ({
        id: variant.id,
        name: variant.name,
        product_id: variant.product_id,
        product_name: variant.product_name,
        // Include any other relevant fields
      }));
      setAvailableVariants(variantsData);
      
      const activeVariantIds = targetVariantIds || finalQuantity?.product_variant_ids || (variantId ? [parseInt(variantId, 10)] : []);
      if (activeVariantIds && activeVariantIds.length > 0) {
        const activeStrArray = activeVariantIds.map(v => String(v));
        const preselectedVariants = variantsData.filter(v => 
          activeStrArray.includes(String(v.id))
        );
        setSelectedVariants(preselectedVariants);
      }
    }
  } catch (error) {
    console.error('Error fetching variants:', error);
    enqueueSnackbar('Failed to fetch product variants', { variant: 'error' });
  }
};


const findLatestWorkflowForProduct = useCallback(async (productId, vId) => {
  try {
    // 1. Try endpoint: /new/workflow-app/process/by-final-product/?final_product_id=${productId}
    if (productId) {
      try {
        const res = await fetcher([`/new/workflow-app/process/by-final-product/?final_product_id=${productId}`]);
        if (res && (res.process_creation_id || res.id)) {
          return res.process_creation_id || res.id;
        }
      } catch (e) {
        console.log('process/by-final-product attempt failed:', e);
      }
    }

    // 2. Try POST endpoint: /new/workflow-app/all/processcreation/show/ with body { final_product_id: productId }
    if (productId) {
      try {
        const res = await postFetcher(['/new/workflow-app/all/processcreation/show/', { final_product_id: productId }]);
        if (Array.isArray(res) && res.length > 0 && res[0].id) {
          return res[0].id;
        }
      } catch (e) {
        console.log('all/processcreation/show attempt failed:', e);
      }
    }

    // 3. Try endpoint: /new/workflow-app/processcreation-final-product-list/?final_product_id=${productId}
    if (productId) {
      try {
        const res = await fetcher([`/new/workflow-app/processcreation-final-product-list/?final_product_id=${productId}`]);
        if (Array.isArray(res) && res.length > 0 && res[0].id) {
          return res[0].id;
        }
      } catch (e) {
        console.log('processcreation-final-product-list attempt failed:', e);
      }
    }

    return null;
  } catch (err) {
    console.error('Error finding workflow for product:', err);
    return null;
  }
}, []);

useEffect(() => {
  const autoLoadWorkflow = async () => {
    try {
      let masterId = primaryid;
      let variantIdToUse = secondaryid;

      const targetProductId = finalProductId || variantId;

      // 1. Resolve master workflow ID (primaryid) from final product if not provided directly
      if (!masterId && targetProductId) {
        try {
          const byProd = await fetcher([`/new/workflow-app/process/by-final-product/?final_product_id=${targetProductId}`]);
          if (byProd && (byProd.process_creation_id || byProd.id)) {
            masterId = byProd.process_creation_id || byProd.id;
          }
        } catch (e) {
          console.log('by-final-product fetch failed:', e);
        }

        if (!masterId) {
          masterId = await findLatestWorkflowForProduct(targetProductId, variantId);
        }
      }

      // 2. Fetch master workflows list to populate "Select Workflow" dropdown
      let workflowsList = savedWorkflows;
      try {
        const payload = targetProductId ? { final_product_id: targetProductId } : {};
        const res = await postFetcher(['/new/workflow-app/all/processcreation/show/', payload]);
        if (Array.isArray(res) && res.length > 0) {
          workflowsList = res;
        }
      } catch (e) {
        console.log('Error fetching all process creations with payload:', e);
      }

      if (!workflowsList || workflowsList.length === 0) {
        try {
          const resAll = await fetcher(['/new/workflow-app/all/processcreation/show/']);
          if (Array.isArray(resAll) && resAll.length > 0) {
            workflowsList = resAll;
          }
        } catch (e) {
          console.log('Error fetching all process creations via GET:', e);
        }
      }

      if (Array.isArray(workflowsList) && workflowsList.length > 0) {
        setSavedWorkflows(workflowsList);
      }

      if (masterId) {
        let masterWf = Array.isArray(workflowsList)
          ? workflowsList.find((w) => w.id == masterId || w.id?.toString() === masterId.toString())
          : null;

        if (masterWf) {
          console.log("Pre-selecting Master Workflow:", masterWf.name);
          setSelectedWorkflow(masterWf.name);
          setselectedmasterworkflow(masterWf.id);
        } else {
          // Add placeholder to savedWorkflows so MUI Select displays option
          const placeholder = { id: masterId, name: `Workflow #${masterId}` };
          setSavedWorkflows((prev) => (prev.some((w) => w.id == masterId) ? prev : [...prev, placeholder]));
          setSelectedWorkflow(placeholder.name);
          setselectedmasterworkflow(masterId);
        }

        // 3. Fetch process variants for this master workflow to populate "Select Workflow Variants" dropdown
        const variants = await fetcher([`/new/workflow-app/process/in/workflow/?process_creation_id=${masterId}`]);
        if (Array.isArray(variants) && variants.length > 0) {
          setvariantoptions(variants);

          const matchingVariant = variantIdToUse
            ? variants.find((v) => v.id == variantIdToUse || v.id?.toString() === variantIdToUse.toString())
            : variants[variants.length - 1] || variants[0];

          if (matchingVariant) {
            console.log("Pre-selecting Workflow Variant:", matchingVariant.name);
            setselectedworkflowvariant(matchingVariant.name);
            await loadWorkflow(matchingVariant.id);
          } else {
            await loadWorkflow(masterId);
          }
        } else {
          await loadWorkflow(masterId);
        }
      }
    } catch (err) {
      console.error('Error in autoLoadWorkflow:', err);
    }
  };
  
  autoLoadWorkflow();
}, [primaryid, secondaryid, finalProductId, variantId, findLatestWorkflowForProduct]);

const loadWorkflow = async (workflowIdParam = null) => {
  console.log("Loading workflow with ID/variant:", workflowIdParam || selectedworkflowvariant || selectedWorkflow);
  
  // Prevent multiple simultaneous loads
  if (isLoadingWorkflow) {
    console.log("Workflow already loading, skipping...");
    return;
  }
  
  setIsLoadingWorkflow(true);
  
  let workflowId = workflowIdParam;

  if (!workflowId) {
    if (selectedworkflowvariant && variantoptions.length > 0) {
      const variantObj = variantoptions.find((v) => v.name === selectedworkflowvariant);
      if (variantObj) {
        workflowId = variantObj.id;
      }
    }
    if (!workflowId && selectedWorkflow && savedWorkflows.length > 0) {
      const wfObj = savedWorkflows.find((w) => w.name === selectedWorkflow);
      if (wfObj) {
        workflowId = wfObj.id;
      }
    }
  }

  if (!workflowId) {
    enqueueSnackbar("Please select a workflow variant to load", { variant: 'error' });
    setIsLoadingWorkflow(false);
    return;
  }

  setIsWorkflowLoaded(true);

  try {
    clearNodeDataCache();
    
    // Fetch workflow data
    const backendData = await fetchWorkflow(workflowId);
    
    if (!backendData) {
      enqueueSnackbar("Workflow data not found!", { variant: 'error' });
      setIsWorkflowLoaded(false);
      setIsLoadingWorkflow(false);
      return;
    }

    const masterIdToSet = selectedmasterworkflow || primaryid || backendData.process_creation_id || (backendData.model ? backendData.id : null) || workflowId;
    setselectedworkflowId(masterIdToSet);

    if (backendData.name && !selectedworkflowvariant) {
      setselectedworkflowvariant(backendData.name);
    }

    // Process final quantity if exists
    if (backendData.final_quantity) {
      setFinalQuantity(backendData.final_quantity);
      const productIds = backendData.final_quantity.product_ids || [];
      const targetVariantIds = backendData.final_quantity.product_variant_ids || (variantId ? [parseInt(variantId, 10)] : []);
      if (productIds.length > 0) {
        await fetchVariantsForProducts(productIds, targetVariantIds);
      } else if (finalProductId) {
        await fetchVariantsForProducts([parseInt(finalProductId, 10)], targetVariantIds);
      }
    } else if (finalProductId) {
      await fetchVariantsForProducts([parseInt(finalProductId, 10)], variantId ? [parseInt(variantId, 10)] : []);
    }
    
    // Process client if exists in loaded workflow
    if (backendData.client || backendData.client_id) {
      const bClientId = backendData.client?.id || backendData.client_id;
      if (bClientId && clientData && clientData.length > 0) {
        const match = clientData.find(
          (c) => c.id === parseInt(bClientId, 10) || c.id?.toString() === bClientId.toString()
        );
        if (match) {
          setSelectedClient(match);
        }
      }
    }

    const allocated_datafrombackend = backendData.allocated_outputs;
    setAllocatedoutputs(allocated_datafrombackend || []);
    setnodefromworkflow(backendData);
    setIsEditing(Boolean(backendData && (backendData.workflow_creation_id || (Array.isArray(variantoptions) && variantoptions.some(v => v.id === backendData.id)))));
    setSavedNonRawNodes(new Set());

    // Handle loading React Flow data or converting from old format
    let nodesToUse = [];
    let edgesToUse = [];

    if (backendData.model && backendData.model.nodes && backendData.model.edges) {
      // New format (React Flow)
      nodesToUse = backendData.model.nodes || [];
      edgesToUse = backendData.model.edges || [];

      // Process each node to ensure correct type and data structure
      nodesToUse = nodesToUse.map(node => {
        // If node type is 'option' in data, set node type to 'option'
        const nodeType = node.data?.type === 'option' ? 'option' : node.type;

        // Extract options configuration
        const optionsConfig = node.data?.options_config || node.data?.options || {};
        const options = optionsConfig.options || [];

        // Get selected options from the node data
        const selectedOptions = node.data?.selected_options || [];

        // Merge selected options with the options config
        const mergedOptions = options.map(opt => {
          // Check if this option is in selectedOptions
          const isSelected = selectedOptions.some(
            selectedOpt => selectedOpt.id === opt.id ||
              selectedOpt.label === opt.label
          );
          return {
            ...opt,
            selected: isSelected || opt.selected || false
          };
        });

        // Update nodeSelectedOptions state
        if (selectedOptions.length > 0) {
          setTimeout(() => {
            setNodeSelectedOptions(prev => ({
              ...prev,
              [node.id]: selectedOptions
            }));
          }, 0);
        }

        return {
          ...node,
          type: nodeType,
          data: {
            ...node.data,
            // Ensure options_config is properly structured with selected options
            options_config: {
              ...optionsConfig,
              options: mergedOptions
            },
            // For backward compatibility
            options: {
              ...optionsConfig,
              options: mergedOptions
            },
            selected_options: selectedOptions,
            selectionType: optionsConfig.selectionType || node.data?.selectionType || 'radio',
            onOptionSelect: handleOptionSelect,
          }
        };
      });

      // compute hasRawMaterial flag for each node and set nodes
      const isExistingWfVariant = Boolean(backendData && (backendData.id || backendData.workflow_creation_id));
      const nodesWithFlags = await Promise.all((nodesToUse || []).map(async (node) => {
        try {
          const hasRaw = await nodeHasRawMaterials(node);
          const backendId = node?.data?.backendId ?? node?.data?.backend_id ?? node?.id;
          const hasUpdated = isExistingWfVariant || clickednodes.includes(Number(backendId)) || !!node?.data?.updated;
          return { ...node, data: { ...node.data, hasRawMaterial: hasRaw, updated: hasUpdated } };
        } catch (err) {
          console.error('Error checking raw materials for node during load:', err);
          return node;
        }
      }));
      setRfNodes(nodesWithFlags);
      setRfEdges(edgesToUse);
      
    } else if (backendData.model && backendData.model.layers) {
      // Old format (react-diagrams) - Convert to React Flow format
      const newNodes = [];
      const newEdges = [];
      const idMap = new Map();

      backendData.model.layers.forEach(layer => {
        if (layer.type === 'diagram-nodes') {
          Object.entries(layer.models).forEach(([backendId, nodeData]) => {
            const reactId = generateCustomId('node');
            idMap.set(backendId, reactId);

            // Determine node type
            const nodeType = nodeData.type === 'diamond' ? 'diamond' :
              nodeData.type === 'option' ? 'option' : 'custom';

            // Extract options configuration if present
            const optionsConfig = nodeData.options_config || nodeData.options || {};
            const options = optionsConfig.options || [];

            // Get selected options from node data
            const selectedOptions = nodeData.selected_options || [];

            // Merge selected options with options config
            const mergedOptions = options.map(opt => {
              const isSelected = selectedOptions.some(
                selectedOpt => selectedOpt.id === opt.id ||
                  selectedOpt.label === opt.label
              );
              return {
                ...opt,
                selected: isSelected || opt.selected || false
              };
            });

            // Update nodeSelectedOptions state
            if (selectedOptions.length > 0) {
              setTimeout(() => {
                setNodeSelectedOptions(prev => ({
                  ...prev,
                  [reactId]: selectedOptions
                }));
              }, 0);
            }

            const newNode = {
              id: reactId,
              type: nodeType,
              position: { x: nodeData.x || 0, y: nodeData.y || 0 },
              data: {
                ...nodeData,
                label: nodeData.name || 'Node',
                backendId: parseInt(nodeData.backendId || nodeData.backend_id || backendId),
                color: nodeData.color || (nodeData.type === 'diamond' ? '#ff6b6b' :
                  nodeData.type === 'option' ? '#f97316' : '#00c0ff'),
                type: nodeData.type,
                // Include options configuration for option nodes with merged selections
                ...(nodeData.type === 'option' && {
                  options_config: {
                    ...optionsConfig,
                    options: mergedOptions
                  },
                  options: {
                    ...optionsConfig,
                    options: mergedOptions
                  },
                  selected_options: selectedOptions,
                  selectionType: optionsConfig.selectionType || 'radio',
                  onOptionSelect: handleOptionSelect,
                }),
              }
            };
            newNodes.push(newNode);
          });
        }

        if (layer.type === 'diagram-links') {
          Object.values(layer.models).forEach(linkData => {
            const sourceNodeId = linkData.source;
            const targetNodeId = linkData.target;

            if (idMap.has(sourceNodeId) && idMap.has(targetNodeId)) {
              const newEdge = {
                id: linkData.id || `e-${sourceNodeId}-${targetNodeId}`,
                source: idMap.get(sourceNodeId),
                target: idMap.get(targetNodeId),
                type: 'default',
                style: { stroke: linkData.color === 'red' ? 'red' : 'grey', strokeWidth: 1 },
                label: linkData.labels?.[0]?.label || '',
                data: {
                  outputId: linkData.labels?.[0]?.outputId || linkData.outputId,
                  isErrorEdge: linkData.color === 'red'
                }
              };
              newEdges.push(newEdge);
            }
          });
        }
      });

      nodesToUse = newNodes;
      edgesToUse = newEdges;

      // compute hasRawMaterial flag for converted nodes
      const convertedWithFlags = await Promise.all((newNodes || []).map(async (node) => {
        try {
          const hasRaw = await nodeHasRawMaterials(node);
          const backendId = node?.data?.backendId ?? node?.data?.backend_id ?? node?.id;
          const hasUpdated = clickednodes.includes(Number(backendId)) || !!node?.data?.updated;
          return { ...node, data: { ...node.data, hasRawMaterial: hasRaw, updated: hasUpdated } };
        } catch (err) {
          console.error('Error checking raw materials for converted node during load:', err);
          return node;
        }
      }));
      setRfNodes(convertedWithFlags);
      setRfEdges(newEdges);
      enqueueSnackbar("Converted legacy workflow to React Flow format.", { variant: 'info' });
    }

    // Fetch edge outputs for each node (use local nodesToUse to avoid relying on async state)
    await Promise.all((nodesToUse || []).map(async (node) => {
      try {
        await fetchEdgeOutputs(node);
      } catch (error) {
        console.error(`[loadWorkflow] Failed to fetch edge outputs for node ${node?.data?.label}:`, error);
      }
    }));

    // Update nodes state to match nodefromworkflow
    let workflowNodes = [];
    if (backendData.model && backendData.model.nodes) {
      // New format
      workflowNodes = backendData.model.nodes.map(node => ({
        id: node.data?.backendId || node.id,
        backendId: node.data?.backendId,
        name: node.data?.label,
        type: node.data?.type || node.type,
        input_foreign: node.data?.input || [],
        output_foreign: node.data?.output || [],
        cost_foreign: node.data?.cost || [],
        options_config: node.data?.options_config,
        selected_options: node.data?.selected_options || [],
      }));
    } else if (backendData.model && backendData.model.layers) {
      // Old format
      const nodeLayer = backendData.model.layers?.find(layer => layer.type === 'diagram-nodes');
      workflowNodes = nodeLayer ? Object.values(nodeLayer.models).map(node => ({
        id: node.id,
        backendId: node.backend_id,
        name: node.name,
        type: node.type,
        input_foreign: node.input_foreign || [],
        output_foreign: node.output_foreign || [],
        cost_foreign: node.cost_foreign || [],
        options_config: node.options_config,
        selected_options: node.selected_options || [],
      })) : [];
    }
    setNodes(workflowNodes);

    const updatedAvailableNodes = (backendData.availableNodes || []).map(node => ({
      ...node,
      backendId: parseInt(node.backend_id || node.backendId, 10),
      inputForeignIds: node.inputForeignIds || [],
      outputForeignIds: node.outputForeignIds || [],
      costForeignIds: node.costForeignIds || []
    }));

    setAvailableNodes(updatedAvailableNodes);

    if (nodesToUse.length > 0) {
      setLastAddedNode(nodesToUse[nodesToUse.length - 1]);
    }

    // Use the local nodesToUse array to update DOM immediately after loading
    nodesToUse.forEach((node) => {
      setTimeout(async () => {
        const nodeElement = document.querySelector(`[data-nodeid="${node.id}"]`);
        if (nodeElement) {
          nodeElement.classList.add('node-with-pointer');
          const hasRawMaterials = await nodeHasRawMaterials(node);
          if (hasRawMaterials) {
            nodeElement.classList.add('node-with-raw-material');
            const indicator = document.createElement('div');
            indicator.className = 'raw-material-indicator';
            indicator.title = 'Please Fill Variants';
            nodeElement.appendChild(indicator);
          }
        }
      }, 100);
    });

    if (nodesToUse.length > 0) {
      setSmartViewOpen(true);
    }

    enqueueSnackbar("Workflow loaded successfully!", { variant: 'success' });

  } catch (error) {
    console.error("Error loading workflow:", error);
    enqueueSnackbar("Error loading workflow!", { variant: 'error' });
    setIsWorkflowLoaded(false);
  } finally {
    setIsLoadingWorkflow(false);
  }
};

  const handleNodeClick = (node) => {
    setSelectedNode(node);
    setEditNodeName(node.data.label);

    setEditRawMaterialInputs([]);
    setEditPreviousNodeInputs([]);
    setEditUtilityInputs([]);
    setEditNodeCost([]);

    setSelectedEdgeId(null);
    setEditEdgeLabel("");

    const edges = rfEdges.filter(e => e.source === node.id || e.target === node.id);
    const edgeDetails = edges.map(edge => {
      const sourceNode = rfNodes.find(n => n.id === edge.source);
      const targetNode = rfNodes.find(n => n.id === edge.target);

      return {
        id: edge.id,
        sourceNode: sourceNode?.data.label || 'Unknown',
        sourcePort: 'Right',
        targetNode: targetNode?.data.label || 'Unknown',
        targetPort: 'Left',
        isErrorEdge: edge.style?.stroke === 'red' || false,
        label: edge.label || '',
        outputId: edge.data?.outputId
      };
    });

    setConnectedEdges(edgeDetails);
    // setDrawerOpen(true);
  };

  const handleCreateEdgeWithOutput = (sourceNode, targetNode, isErrorEdge = false) => {
    if (!sourceNode || !targetNode || sourceNode.id === targetNode.id) {
      enqueueSnackbar("Invalid node selection for edge!", { variant: 'error' });
      setIsErrorEdgeMode(false);
      setErrorSourceNode(null);
      setShowOutputSelectionDialog(false);
      setSourceNodeForEdge(null);
      setTargetNodeForEdge(null);
      setSelectedOutput(null);
      setEdgeCreationType(null);
      return;
    }

    try {
      const newEdge = {
        id: `e-${sourceNode.id}-${targetNode.id}-${Date.now()}`,
        source: sourceNode.id,
        target: targetNode.id,
        type: 'default',
        style: { stroke: isErrorEdge ? 'red' : 'grey', strokeWidth: 1 },
        label: isErrorEdge ? 'Error' : (selectedOutput?.label || ''),
        data: {
          outputId: selectedOutput?.value,
          ...(isErrorEdge ? { isErrorEdge: true } : {})
        }
      };

      setRfEdges((eds) => addEdge(newEdge, eds));

      enqueueSnackbar(`Edge created from ${sourceNode.data.label} to ${targetNode.data.label}${selectedOutput ? ` with output "${selectedOutput.label}"` : ''}`, { variant: 'success' });

      if (selectedNode === sourceNode) {
        handleNodeClick(sourceNode);
      }
    } catch (error) {
      enqueueSnackbar("Failed to create edge!", { variant: 'error' });
      console.error(error);
    }

    setIsErrorEdgeMode(false);
    setErrorSourceNode(null);
    setShowOutputSelectionDialog(false);
    setSourceNodeForEdge(null);
    setTargetNodeForEdge(null);
    setSelectedOutput(null);
    setEdgeCreationType(null);
  };

  const handleCreateErrorEdge = async (sourceNode, targetNode) => {
    if (!sourceNode || !targetNode || sourceNode.id === targetNode.id) {
      enqueueSnackbar("Invalid node selection for error edge!", { variant: 'error' });
      setIsErrorEdgeMode(false);
      setErrorSourceNode(null);
      return;
    }

    try {
      const newEdge = {
        id: `e-${sourceNode.id}-${targetNode.id}-${Date.now()}`,
        source: sourceNode.id,
        target: targetNode.id,
        type: 'default',
        style: { stroke: 'red', strokeWidth: 1 },
        label: 'Error',
        data: { isErrorEdge: true }
      };

      setRfEdges((eds) => addEdge(newEdge, eds));

      enqueueSnackbar(`Error edge created from ${sourceNode.data.label} to ${targetNode.data.label}`, { variant: 'success' });

      if (selectedNode === sourceNode) {
        handleNodeClick(sourceNode);
      }
    } catch (error) {
      enqueueSnackbar("Failed to create error edge!", { variant: 'error' });
      console.error(error);
    }

    setIsErrorEdgeMode(false);
    setErrorSourceNode(null);
  };

  const fetchnodedata = async () => {
    const id = selectedNode?.data?.backendId;
    if (!id) {
      return;
    }

    if (nodeDataCache.has(id)) {
      const cachedData = nodeDataCache.get(id);
      setNodeData(cachedData);

      if (cachedData && cachedData.input_foreign && cachedData.input_foreign.length > 0) {
        const inputData = cachedData.input_foreign[0];

        if (inputData.value && inputData.value.product_ids) {
          const selectedRawMaterials = inventoryOptions.filter(option =>
            inputData.value.product_ids.includes(Number(option.value))
          );
          setEditRawMaterialInputs(selectedRawMaterials);
        }

        if (inputData.value && inputData.value.utility_ids) {
          const selectedUtilities = utilityOptions.filter(option =>
            inputData.value.utility_ids.includes(Number(option.id))
          );
          setEditUtilityInputs(selectedUtilities);
        }
      }

      if (cachedData.cost_foreign && cachedData.cost_foreign.length > 0) {
        const selectedCosts = costOptions.filter(option =>
          cachedData.cost_foreign.some(cost => cost.id === Number(option.id))
        );
        setEditNodeCost(selectedCosts || []);
      } else {
        setEditNodeCost([]);
      }

      return;
    }

    setLoadingNodeData(true);
    try {
      const res = await fetcher([`/new/workflow-app/node/${id}/`]);

      setNodeDataCache(prev => {
        const newCache = new Map(prev);
        newCache.set(id, res);
        return newCache;
      });
      setNodeData(res);

      if (res && res.input_foreign && res.input_foreign.length > 0) {
        const inputData = res.input_foreign[0];

        if (inputData.value && inputData.value.product_ids) {
          const selectedRawMaterials = inventoryOptions.filter(option =>
            inputData.value.product_ids.includes(Number(option.value))
          );
          setEditRawMaterialInputs(selectedRawMaterials);
        }

        if (inputData.value && inputData.value.utility_ids) {
          const selectedUtilities = utilityOptions.filter(option =>
            inputData.value.utility_ids.includes(Number(option.id))
          );
          setEditUtilityInputs(selectedUtilities);
        }
      }

      if (res.cost_foreign && res.cost_foreign.length > 0) {
        const selectedCosts = costOptions.filter(option =>
          res.cost_foreign.some(cost => cost.id === Number(option.id))
        );
        setEditNodeCost(selectedCosts || []);
      } else {
        setEditNodeCost([]);
      }
    } catch (error) {
      setEditNodeCost([]);
    } finally {
      setLoadingNodeData(false);
    }
  };

  useEffect(() => {
    if (drawerOpen && selectedNode) {
      fetchnodedata();
    }
  }, [drawerOpen, selectedNode]);

  const handleNodeSelection = useCallback((node) => {
    if (isErrorEdgeModeRef.current && errorSourceNodeRef.current) {
      handleCreateErrorEdge(errorSourceNodeRef.current, node);
    } else {
      handleNodeClick(node);
      // setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    handleNodeSelectionRef.current = handleNodeSelection;
  }, [handleNodeSelection]);

  const handleErrorEdgeButtonClick = () => {
    if (!selectedNode) return;

    setErrorSourceNode(selectedNode);
    setIsErrorEdgeMode(true);
    setDrawerOpen(false);

    enqueueSnackbar(`Error edge mode activated! Click on another node to create an error edge from "${selectedNode.data.label}"`, { variant: 'success' });
  };

  const onConnect = useCallback((params) => {
    const sourceNode = rfNodes.find(n => n.id === params.source);
    const targetNode = rfNodes.find(n => n.id === params.target);

    if (isErrorEdgeMode) {
      handleCreateErrorEdge(sourceNode, targetNode);
      return;
    }

    setSourceNodeForEdge(sourceNode);
    setTargetNodeForEdge(targetNode);
    setEdgeCreationType('manual');

    fetchEdgeOutputs(sourceNode).then(() => {
      sessionStorage.setItem('pendingLinkId', `e-${params.source}-${params.target}-${Date.now()}`);
      setShowOutputSelectionDialog(true);
    }).catch(error => {
      console.error('Error fetching outputs:', error);
    });
  }, [isErrorEdgeMode, rfNodes]);

  const onNodeClickHandler = useCallback((event, node) => {
    handleNodeSelection(node);
  }, [handleNodeSelection]);

  const onInit = useCallback((instance) => {
    reactFlowInstance.current = instance;
  }, []);

const handleSaveFinalQuantity = async () => {
  

  try {
    const updatedFinalQuantity = {
      ...finalQuantity,
      product_variant_ids: selectedVariants.map(v => v.id)
    };

    // Call API to update the workflow with selected variants
    // const payload = {
    //   workflow_id: selectedworkflowId,
    //   final_quantity: updatedFinalQuantity
    // };

    // const response = await postFetcher([
    //   `${process.env.REACT_APP_HOST_API}/new/workflow-app/react-flow/workflowcreation/update-final-quantity/`,
    //   payload
    // ]);

   
      setFinalQuantity(updatedFinalQuantity);
      enqueueSnackbar('Product variants saved successfully!', { variant: 'success' });
      setShowVariantDialog(false);
  
  } catch (error) {
    console.error('Error saving final quantity:', error);
    enqueueSnackbar('Failed to save product variants', { variant: 'error' });
  }
};

  const handleAddNode = async () => {
    if (!nodeName.trim()) return;

    const newPayload = {
      name: nodeName,
      type: nodeType,
      input_foreign_data: {
        name: inputname,
        formula: inputFormula,
        // product_ids: rawMaterialInputs.map(input => Number(input.value)).filter(id => !Number.isNaN(id)),
        // use latest allVariantIds (synced into combinedVariantIds above) just to be safe
        product_variant_ids: combinedVariantIds,
        utility_ids: utilityInputs.map(input => (input.id)),
      },
      output_foreign_ids: outputids,
      cost_foreign_ids: nodeCost.map(cost => cost.id),
    };

    try {
      const res = await postFetcher(['/new/workflow-app/nodes/create/', newPayload]);

      const newNodeId = res.id.toString();

      // Add to nodes state
      const newNodeData = {
        ...res,
        id: newNodeId,
        backendId: res.id,
        name: nodeName,
        color: nodeColor,
        type: nodeType,
        input_foreign: res.input_foreign,
        output_foreign: newPayload.output_foreign_ids,
        cost_foreign: newPayload.cost_foreign_ids
      };

      setNodes(prev => [...prev, newNodeData]);

      if (res && res.id) {
        const newNode = {
          id: newNodeId,
          backendId: res.id,
          name: nodeName,
          color: nodeColor,
          type: nodeType,
          input: res.input_foreign,
          output: outputids,
          cost: nodeCost.map(cost => cost.id),
          ...(nodeType === 'option' && {
            onOptionSelect: handleOptionSelect,
          }),
        };

        setAvailableNodes(prev => [...prev, newNode]);
        setIsWorkflowLoaded(false);

        setNodeName("");
        setInputname("");
        setNodeColor("#00c0ff");
        setNodeType("default");
        setRawMaterialInputs([]);
        setUtilityInputs([]);
        setNodeCost([]);
        setNodeInputType("raw");
        setShowDialog(false);
      }
    } catch (error) {
      console.error('Error creating node:', error);
      enqueueSnackbar('Failed to create node', { variant: 'error' });
    }
  };


  const updateCombinedVariantIds = (newVariantMap, isAllocable = false) => {
    const newIds = Object.values(newVariantMap || {})
      .flat()
      .filter(v => v?.id)
      .map(v => v.id);

    setCombinedVariantIds(prev => {
      return [...new Set([...prev, ...newIds])];
    });
  };

  // For regular variants
  const handleRegularVariantsSaved = (variantMap) => {
    updateCombinedVariantIds(variantMap);
  };

  // For allocable variants
  const handleAllocableVariantsSaved = (allocableMap) => {
    updateCombinedVariantIds(allocableMap);
  };



  const handleDragStart = (e, node) => {
    if (isWorkflowLoaded) {
      e.preventDefault();
      return;
    }

    e.dataTransfer.setData('nodeData', JSON.stringify(node));
    e.dataTransfer.setData('application/reactflow', node.type);
    e.dataTransfer.effectAllowed = 'copy';
    setIsDragging(true);
  };

  const handleDragOver = (e) => {
    if (isWorkflowLoaded) {
      return;
    }

    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const handleDrop = async (e) => {
    if (isWorkflowLoaded) {
      setIsDragging(false);
      e.preventDefault();
      return;
    }

    e.preventDefault();
    setIsDragging(false);

    const nodeData1 = JSON.parse(e.dataTransfer.getData('nodeData'));
    if (!nodeData1) return;

    const position = screenToFlowPosition({
      x: e.clientX,
      y: e.clientY,
    });

    const newNode = {
      id: generateCustomId('node'),
      type: nodeData1.type === 'diamond' ? 'diamond' :
        nodeData1.type === 'option' ? 'option' : 'custom',
      position,
      data: {
        label: nodeData1.name || "New Node",
        backendId: nodeData1.backendId || nodeData1.backend_id,
        color: nodeData1.color || (nodeData1.type === 'diamond' ? '#ff6b6b' :
          nodeData1.type === 'option' ? '#f97316' : '#00c0ff'),
        type: nodeData1.type,
        rawInputs: nodeData1.rawInputs || [],
        nodeInputs: nodeData1.nodeInputs || [],
        utilityInputs: nodeData1.utilityInputs || [],
        output: nodeData1.output || '',
        cost: nodeData1.cost || [],
        // Add options configuration for option nodes
        ...(nodeData1.type === 'option' && {
          options_config: nodeData1.options_config || {},
          options: nodeData1.options || {},
          onOptionSelect: handleOptionSelect, // Add the callback
        }),
        ...nodeData1
      },
    };

    setRfNodes((nds) => nds.concat(newNode));
    setNodeListVersion(prev => prev + 1);
    setTargetNodeForEdge(newNode);

    const updatedNode = {
      ...nodeData1,
      id: newNode.id,
      backendId: nodeData1.backendId,
    };

    setAvailableNodes(prev => prev.map(node => node.id === nodeData1.id ? updatedNode : node));
    setIsWorkflowLoaded(false);

    setTimeout(async () => {
      const nodeElement = document.querySelector(`[data-nodeid="${newNode.id}"]`);
      if (nodeElement) {
        nodeElement.classList.add('node-with-pointer');
        const hasRawMaterials = await nodeHasRawMaterials(newNode);
        if (hasRawMaterials) {
          nodeElement.classList.add('node-with-raw-material');
          const indicator = document.createElement('div');
          indicator.className = 'raw-material-indicator';
          indicator.title = 'Please Fill Variants';
          nodeElement.appendChild(indicator);
        }
      }
    }, 100);

    if (nodeData1.nodeInputs && nodeData1.nodeInputs.length > 0 && !isErrorEdgeMode) {
      nodeData1.nodeInputs.forEach(input => {
        const sourceNode = rfNodes.find(node => node.data.label === input.label);
        if (sourceNode) {
          setSourceNodeForEdge(sourceNode);
          setTargetNodeForEdge(newNode);
          setEdgeCreationType('auto');

          setTimeout(() => {
            fetchEdgeOutputs(sourceNode).then(() => {
              if (edgeOutputs.length > 0) {
                setShowOutputSelectionDialog(true);
              }
            });
          }, 100);
        }
      });
    } else if (lastAddedNode && lastAddedNode.id !== newNode.id && !isErrorEdgeMode) {
      setSourceNodeForEdge(lastAddedNode);
      setTargetNodeForEdge(newNode);
      setEdgeCreationType('auto');

      setTimeout(() => {
        fetchEdgeOutputs(lastAddedNode).then(() => {
          if (edgeOutputs.length > 0) {
            setShowOutputSelectionDialog(true);
          }
        });
      }, 100);
    }

    setLastAddedNode(newNode);
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  const handleCancel = () => {
    setShowDialog(false);
  };

  const getTargetNodes = (diamondNode) => {
    if (!diamondNode || !reactFlowInstance.current) return [];

    const targetNodes = [];
    const edges = rfEdges.filter(e => e.source === diamondNode.id);

    edges.forEach(edge => {
      const targetNode = rfNodes.find(n => n.id === edge.target);
      if (targetNode) {
        targetNodes.push(targetNode);
      }
    });

    return targetNodes;
  };


  const saveNonRawMaterialNodes = async (updatedNodeId) => {
    setIsSavingingNodes(true);

    try {
      const defaultNodesToSave = [];
      const diamondNodesToSave = [];

      for (const node of rfNodes) {
        const nodeId = node.data.backendId;
        if (!nodeId || nodeId === updatedNodeId) continue;

        const hasRawMaterials = await nodeHasRawMaterials(node);
        if (hasRawMaterials) continue;

        // CRITICAL: Skip if node has already been saved
        if (savedNonRawNodes.has(nodeId)) {
          console.log(`Skipping already saved node: ${node.data.label} (${nodeId})`);
          continue;
        }

        if (node.type === 'diamond') {
          diamondNodesToSave.push(node);
        } else {
          defaultNodesToSave.push(node);
        }
      }

      // If no nodes to save, exit early
      if (defaultNodesToSave.length === 0 && diamondNodesToSave.length === 0) {
        console.log('No unsaved non-raw nodes to save');
        setIsSavingingNodes(false);
        return;
      }

      const savedDefaultNodeMap = new Map();

      for (const node of defaultNodesToSave) {
        const oldBackendId = node.data.backendId;
        const currentNodeData = await fetcher([`/new/workflow-app/node/${oldBackendId}/`]);
        console.log('currentnodedata', currentNodeData);

        const formData = new FormData();
        formData.append('name', currentNodeData.name);
        if (currentNodeData.type) formData.append('type', currentNodeData.type);

        const inputForeignData = {
          name: currentNodeData.input_foreign?.[0]?.name || currentNodeData.name + " Input",
          product_ids: currentNodeData.input_foreign?.[0]?.value?.product_ids || [],
          utility_ids: currentNodeData.input_foreign?.[0]?.value?.utility_ids || [],
          product_variant_ids: currentNodeData.input_foreign?.[0]?.value?.product_variant_ids || [],
        };
        formData.append('input_foreign_data', JSON.stringify(inputForeignData));
        formData.append('output_foreign_ids', JSON.stringify(currentNodeData.output_foreign?.map(o => o.id) || []));
        formData.append('cost_foreign_ids', JSON.stringify(currentNodeData.cost_foreign?.map(c => c.id) || []));
        formData.append('machine_ids', JSON.stringify(currentNodeData.machine?.map(m => m.id) || []));
        formData.append('user_ids', JSON.stringify(currentNodeData?.users || []));

        // Handle options_node with selected: true for selected options
        if (node.type === 'option') {
          const nodeSelectedOpts = nodeSelectedOptions[node.id] || [];
          const currentOptionsConfig = node.data?.options_config || node.data?.options || {};
          const allOptions = currentOptionsConfig.options || [];
          const selectedOptionIds = new Set(nodeSelectedOpts.map(opt => opt.id));
          const updatedOptions = allOptions.map(opt => ({
            ...opt,
            selected: selectedOptionIds.has(opt.id)
          }));

          const optionsNode = {
            selectionType: currentOptionsConfig.selectionType || 'radio',
            options: updatedOptions
          };

          formData.append('option_config', JSON.stringify(optionsNode));

          if (nodeSelectedOpts.length > 0) {
            formData.append('selected_options', JSON.stringify(nodeSelectedOpts.map(opt => ({
              id: opt.id,
              label: opt.label,
              value: opt.value,
              costIds: opt.costIds || [],
              costs: opt.costs || []
            }))));
          }
        } else if (currentNodeData?.options_node) {
          formData.append('option_config', JSON.stringify(currentNodeData.options_node));
        }

        if (currentNodeData.attachments?.length > 0) {
          for (const att of currentNodeData.attachments) {
            if (att.file && typeof att.file === 'string') {
              const blob = await fetch(att.file).then(r => r.blob());
              const file = new File([blob], att.file_name || 'file');
              formData.append('files', file);
            }
          }
        }

        const res = await axiosInstance.post(`/new/workflow-app/nodes/create/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const newBackendId = res.data.id;

        savedDefaultNodeMap.set(oldBackendId, newBackendId);

        // Mark this node as saved
        // Mark this node as saved with the NEW backend ID
        setSavedNonRawNodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(oldBackendId); // Remove old ID if it exists
          newSet.add(newBackendId);    // Add the NEW backend ID
          return newSet;
        });

        // Get the current selected options BEFORE updating
        const currentSelectedOpts = nodeSelectedOptions[node.id] || [];

        setRfNodes(nds => nds.map(n => {
          if (n.id === node.id) {
            const currentOptionsConfig = n.data?.options_config || n.data?.options || {};
            const allOptions = currentOptionsConfig.options || [];
            const selectedOptionIds = new Set(currentSelectedOpts.map(opt => opt.id));
            const updatedOptions = allOptions.map(opt => ({
              ...opt,
              selected: selectedOptionIds.has(opt.id)
            }));

            const updatedNodeData = {
              ...n.data,
              backendId: newBackendId,
              color: "#4CAF50",
              updated: true,
              options_config: {
                ...currentOptionsConfig,
                options: updatedOptions
              },
              options: {
                ...currentOptionsConfig,
                options: updatedOptions
              },
              selected_options: currentSelectedOpts,
              onOptionSelect: handleOptionSelect
            };

            return {
              ...n,
              data: updatedNodeData
            };
          }
          return n;
        }));

        setNodes(prev => prev.map(n =>
          n.backendId === oldBackendId
            ? {
              ...n,
              backendId: newBackendId,
              id: newBackendId.toString(),
              color: "#4CAF50",
              ...(node.type === 'option' && {
                options_node: {
                  selectionType: node.data?.options_config?.selectionType || 'radio',
                  options: (node.data?.options_config?.options || []).map(opt => ({
                    ...opt,
                    selected: (nodeSelectedOptions[node.id] || []).some(selectedOpt => selectedOpt.id === opt.id)
                  }))
                },
                selected_options: nodeSelectedOptions[node.id] || []
              })
            }
            : n
        ));

        setclickednodes(prev => [...new Set([...prev, newBackendId])]);

        // Update nodeSelectedOptions with new node ID
        if (node.type === 'option' && currentSelectedOpts.length > 0) {
          setNodeSelectedOptions(prev => {
            const newState = { ...prev };
            delete newState[node.id];
            newState[newBackendId.toString()] = currentSelectedOpts;
            return newState;
          });
        }
      }

      // Handle diamond nodes similarly with saved check
      for (const diamondNode of diamondNodesToSave) {
        const oldBackendId = diamondNode.data.backendId;

        // Skip if already saved
        if (savedNonRawNodes.has(oldBackendId)) {
          console.log(`Skipping already saved diamond node: ${diamondNode.data.label}`);
          continue;
        }

        const currentNodeData = await fetcher([`/new/workflow-app/node/${oldBackendId}/`]);

        const targetNodes = getTargetNodes(diamondNode);
        const targetBackendIds = targetNodes
          .map(targetNode => {
            const oldId = targetNode.data.backendId;
            return savedDefaultNodeMap.get(oldId) || oldId;
          })
          .filter(Boolean);

        const fallback = [{
          node_ids: targetBackendIds
        }];

        const formData = new FormData();
        formData.append('name', currentNodeData.name);
        formData.append('type', 'diamond');

        const inputForeignData = {
          name: currentNodeData.input_foreign?.[0]?.name || currentNodeData.name + " Input",
          product_ids: currentNodeData.input_foreign?.[0]?.value?.product_ids || [],
          utility_ids: currentNodeData.input_foreign?.[0]?.value?.utility_ids || [],
          product_variant_ids: currentNodeData.input_foreign?.[0]?.value?.product_variant_ids || [],
        };
        formData.append('input_foreign_data', JSON.stringify(inputForeignData));
        formData.append('output_foreign_ids', JSON.stringify(currentNodeData.output_foreign?.map(o => o.id) || []));
        formData.append('cost_foreign_ids', JSON.stringify(currentNodeData.cost_foreign?.map(c => c.id) || []));
        formData.append('machine_ids', JSON.stringify(currentNodeData.machine?.map(m => m.id) || []));
        formData.append('user_ids', JSON.stringify(currentNodeData?.users || []));
        formData.append('fallback', JSON.stringify(fallback));

        if (currentNodeData?.options_node) {
          formData.append('options_node', JSON.stringify(currentNodeData.options_node));
        }

        if (currentNodeData.attachments?.length > 0) {
          for (const att of currentNodeData.attachments) {
            if (att.file && typeof att.file === 'string') {
              const blob = await fetch(att.file).then(r => r.blob());
              const file = new File([blob], att.file_name || 'file');
              formData.append('files', file);
            }
          }
        }

        const res = await axiosInstance.post(`/new/workflow-app/nodes/create/`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' }
        });

        const newBackendId = res.data.id;

        // Mark diamond node as saved// Mark diamond node as saved with the NEW backend ID
        setSavedNonRawNodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(oldBackendId); // Remove old ID if it exists
          newSet.add(newBackendId);    // Add the NEW backend ID
          return newSet;
        }); setSavedNonRawNodes(prev => new Set([...prev, oldBackendId]));

        setRfNodes(nds => nds.map(n => {
          if (n.id === diamondNode.id) {
            return {
              ...n,
              data: {
                ...n.data,
                backendId: newBackendId,
                color: "#4CAF50"
              }
            };
          }
          return n;
        }));

        setNodes(prev => prev.map(n =>
          n.backendId === oldBackendId
            ? { ...n, backendId: newBackendId, id: newBackendId.toString(), color: "#4CAF50" }
            : n
        ));
        setclickednodes(prev => [...new Set([...prev, newBackendId])]);
      }

      enqueueSnackbar(
        `Auto-saved ${defaultNodesToSave.length + diamondNodesToSave.length} nodes`,
        { variant: 'success' }
      );

    } catch (error) {
      console.error('Error auto-saving nodes:', error);
      enqueueSnackbar('Failed to auto-save nodes', { variant: 'error' });
    } finally {
      setIsSavingingNodes(false);
    }
  };

  const handleUpdateNode = async () => {
    if (!selectedNode || !editNodeName.trim()) {
      return;
    }

    try {
      const oldBackendId = selectedNode.data.backendId;
      const oldNodeId = selectedNode.id;

      // Get selected options for this node
      const nodeSelectedOpts = nodeSelectedOptions[oldNodeId] || [];

      const formData = new FormData();
      formData.append('name', editNodeName);

      const inputForeignData = {
        name: nodeData?.input_foreign?.[0]?.name || editNodeName + " Input",
        product_ids: editRawMaterialInputs.map(input => Number(input.value)).filter(id => !Number.isNaN(id)),
        utility_ids: utitilityfromdrawer,
        product_variant_ids: combinedVariantIds,
      };
      formData.append('input_foreign_data', JSON.stringify(inputForeignData));
      formData.append('output_foreign_ids', JSON.stringify(editoutputids.map(output => Number(output.id))));
      formData.append('cost_foreign_ids', JSON.stringify(editNodeCost.map(cost => cost.id)));
      formData.append('machine_ids', JSON.stringify(nodeData?.machine?.map(machine => machine.id) || []));
      formData.append('user_ids', JSON.stringify(nodeData?.users || []));

      // Handle options_node with selected: true only for selected options
      if (selectedNode.type === 'option') {
        const currentOptionsConfig = selectedNode.data?.options_config || selectedNode.data?.options || {};
        const allOptions = currentOptionsConfig.options || [];
        const selectedOptionIds = new Set(nodeSelectedOpts.map(opt => opt.id));
        const updatedOptions = allOptions.map(opt => ({
          ...opt,
          selected: selectedOptionIds.has(opt.id)
        }));

        const optionsNode = {
          selectionType: currentOptionsConfig.selectionType || 'radio',
          options: updatedOptions
        };

        formData.append('option_config', JSON.stringify(optionsNode));
        formData.append('type', 'option')

        if (nodeSelectedOpts.length > 0) {
          formData.append('selected_options', JSON.stringify(nodeSelectedOpts.map(opt => ({
            id: opt.id,
            label: opt.label,
            value: opt.value,
            costIds: opt.costIds || [],
            costs: opt.costs || []
          }))));
        }
      } else if (nodeData?.options_node) {
        formData.append('option_config', JSON.stringify(nodeData.options_node));
      }

      if (nodeData?.attachments?.length > 0) {
        for (const attachment of nodeData.attachments) {
          if (attachment.file && typeof attachment.file === "string") {
            try {
              const response = await fetch(attachment.file);
              if (!response.ok) {
                throw new Error(`Failed to fetch file: ${response.status} ${response.statusText}`);
              }
              const blob = await response.blob();
              const fileName = attachment.file_name || `file_${Date.now()}`;
              const file = new File([blob], fileName, { type: blob.type });
              formData.append('files', file);
            } catch (fileError) {
              console.error(`Error converting file URL to Blob:`, fileError);
            }
          } else if (attachment.file) {
            formData.append('files', attachment.file);
          }
        }
      }

      let res;
      const config = {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      };

      if (isEditing) {
        res = await axiosInstance.put(`/new/workflow-app/nodes/create/${oldBackendId}/`, formData, config);
      } else {
        res = await axiosInstance.post(`/new/workflow-app/nodes/create/`, formData, config);
      }

      const newBackendId = res.data.id;

      // REMOVE THIS LINE - savedDefaultNodeMap is not defined here
      // savedDefaultNodeMap.set(oldBackendId, newBackendId);

      // Update edges that reference this node
      setRfEdges(prevEdges => prevEdges.map(edge => {
        if (edge.source === oldNodeId) {
          return { ...edge, source: newBackendId.toString() };
        }
        if (edge.target === oldNodeId) {
          return { ...edge, target: newBackendId.toString() };
        }
        return edge;
      }));

      // Get updated options with selected flags for updating the node data
      let updatedOptionsWithSelection = [];
      if (selectedNode.type === 'option') {
        const currentOptionsConfig = selectedNode.data?.options_config || selectedNode.data?.options || {};
        const allOptions = currentOptionsConfig.options || [];
        const selectedOptionIds = new Set(nodeSelectedOpts.map(opt => opt.id));

        updatedOptionsWithSelection = allOptions.map(opt => ({
          ...opt,
          selected: selectedOptionIds.has(opt.id)
        }));
      }

      setRfNodes(nds => nds.map(n => {
        if (n.id === selectedNode.id) {
          const currentOptionsConfig = n.data?.options_config || n.data?.options || {};
          const allOptions = currentOptionsConfig.options || [];
          const selectedOptionIds = new Set(nodeSelectedOpts.map(opt => opt.id));
          const updatedOptions = allOptions.map(opt => ({
            ...opt,
            selected: selectedOptionIds.has(opt.id)
          }));

          const updatedNodeData = {
            ...n.data,
            label: editNodeName,
            backendId: newBackendId,
            color: "#4CAF50",
            options_config: {
              ...currentOptionsConfig,
              options: updatedOptions
            },
            options: {
              ...currentOptionsConfig,
              options: updatedOptions
            },
            selected_options: nodeSelectedOpts,
            onOptionSelect: handleOptionSelect
          };

          return {
            ...n,
            id: newBackendId.toString(),
            data: updatedNodeData
          };
        }
        return n;
      }));

      // Update nodeSelectedOptions with new ID
      if (nodeSelectedOpts.length > 0) {
        setNodeSelectedOptions(prev => {
          const newState = { ...prev };
          delete newState[oldNodeId];
          newState[newBackendId.toString()] = nodeSelectedOpts;
          return newState;
        });
      }

      setclickednodes(prev => {
        const updated = prev.includes(newBackendId) ? prev : [...prev, newBackendId];
        return updated;
      });

      // Mark the React Flow node as updated and set raw-material flag if applicable
      try {
        const updatedNodeCheck = {
          ...selectedNode,
          id: newBackendId.toString(),
          data: { ...selectedNode.data, backendId: newBackendId, label: editNodeName }
        };
        const hasRaw = await nodeHasRawMaterials(updatedNodeCheck);
        setRfNodes(nds => nds.map(n => n.id === newBackendId.toString()
          ? { ...n, data: { ...n.data, updated: true, hasRawMaterial: hasRaw } }
          : n
        ));
      } catch (err) {
        console.error('Error marking updated/raw flags for node:', err);
      }

      // Update nodes state properly
      setNodes(prev => {
        const existingNodeIndex = prev.findIndex(node =>
          node.backendId === oldBackendId || node.id === oldBackendId?.toString()
        );

        const newNodeData = {
          id: newBackendId.toString(),
          backendId: newBackendId,
          name: editNodeName,
          color: "#4CAF50",
          type: selectedNode.type,
          input_foreign: res.data.input_foreign || [],
          output_foreign: inputForeignData.product_ids,
          cost_foreign: editNodeCost.map(cost => cost.id),
          selected_options: nodeSelectedOpts,
        };

        if (selectedNode.type === 'option') {
          const currentOptionsConfig = selectedNode.data?.options_config || selectedNode.data?.options || {};
          const allOptions = currentOptionsConfig.options || [];
          const selectedOptionIds = new Set(nodeSelectedOpts.map(opt => opt.id));
          const updatedOptions = allOptions.map(opt => ({
            ...opt,
            selected: selectedOptionIds.has(opt.id)
          }));

          newNodeData.options_node = {
            selectionType: currentOptionsConfig.selectionType || 'radio',
            options: updatedOptions
          };
        }

        if (existingNodeIndex >= 0) {
          const updated = [...prev];
          updated[existingNodeIndex] = newNodeData;
          return updated;
        } else {
          return [...prev, newNodeData];
        }
      });

      // Also update availableNodes
      setAvailableNodes(prev => {
        const existingNodeIndex = prev.findIndex(node =>
          node.backendId === oldBackendId || node.id === oldBackendId?.toString()
        );

        const newNodeData = {
          id: newBackendId.toString(),
          backendId: newBackendId,
          name: editNodeName,
          color: "#4CAF50",
          type: selectedNode.type,
          input: res.data.input_foreign || [],
          output: inputForeignData.product_ids,
          cost: editNodeCost.map(cost => cost.id),
          selected_options: nodeSelectedOpts,
        };

        if (existingNodeIndex >= 0) {
          const updated = [...prev];
          updated[existingNodeIndex] = newNodeData;
          return updated;
        } else {
          return [...prev, newNodeData];
        }
      });

      setTimeout(async () => {
        const nodeElement = document.querySelector(`[data-nodeid="${selectedNode.id}"]`);
        if (nodeElement) {
          const hasRawMaterials = await nodeHasRawMaterials(selectedNode);

          if (hasRawMaterials) {
            nodeElement.classList.add('node-with-raw-material');
            const indicator = document.createElement('div');
            indicator.className = 'raw-material-indicator';
            indicator.title = 'Please Fill Variants';
            nodeElement.appendChild(indicator);
          } else {
            nodeElement.classList.remove('node-with-raw-material');
            const existingIndicator = nodeElement.querySelector('.raw-material-indicator');
            if (existingIndicator) {
              existingIndicator.remove();
            }
          }
        }
      }, 100);

      enqueueSnackbar(`Node "${editNodeName}" ${isEditing ? 'updated' : 'created'} successfully!`, { variant: 'success' });

      const currentHasRawMaterials = await nodeHasRawMaterials(selectedNode);
      if (currentHasRawMaterials) {
        await saveNonRawMaterialNodes(newBackendId);
      }

      // FIX: Move this check inside the non-raw node condition
      const hasRawMaterials = await nodeHasRawMaterials(selectedNode);

      if (!hasRawMaterials) {
        setSavedNonRawNodes(prev => {
          const newSet = new Set(prev);
          newSet.delete(oldBackendId); // Remove old ID if exists
          newSet.add(newBackendId);    // Add new ID
          return newSet;
        });
      }

    } catch (error) {
      console.error('Error saving non-raw material nodes:', error);
      enqueueSnackbar(`Failed to ${isEditing ? 'update' : 'create'} node`, { variant: 'error' });
    }
  };


  const handleDeleteSelectedNode = () => {
    if (!selectedNode) return;

    const outputIdsToFree = [];
    const edgesToRemove = rfEdges.filter(e => e.source === selectedNode.id || e.target === selectedNode.id);

    edgesToRemove.forEach(edge => {
      if (!outputIdsToFree.includes(edge.data?.outputId)) {
        outputIdsToFree.push(edge.data?.outputId);
      }
    });

    setAllocatedoutputs(prev => prev.filter(id => !outputIdsToFree.includes(id)));
    setRfEdges(eds => eds.filter(e => e.source !== selectedNode.id && e.target !== selectedNode.id));
    setRfNodes(nds => nds.filter(n => n.id !== selectedNode.id));

    const deletedNodeName = selectedNode.data.label;
    const deletedNodeBackendId = selectedNode.data.backendId?.toString();

    // MOVE THIS AFTER deletedNodeBackendId is defined
    setSavedNonRawNodes(prev => {
      const newSet = new Set(prev);
      newSet.delete(deletedNodeBackendId);
      return newSet;
    });

    setAvailableNodes(prev => prev.filter(node => node.name !== deletedNodeName));

    if (targetNodeForEdge && targetNodeForEdge.id === selectedNode.id) {
      setTargetNodeForEdge(null);
    }

    setSelectedNode(null);
    setConnectedEdges([]);
    setDrawerOpen(false);
    setLastAddedNode(null);
    setSelectedEdgeId(null);
    setEditEdgeLabel("");

    setIsWorkflowLoaded(false);

    enqueueSnackbar(`Node "${deletedNodeName}" deleted successfully!`, { variant: 'success' });
  };

  const clearNodeDataCache = () => {
    setNodeDataCache(new Map());
    setNodeData(null);
  };

  const handleAddRawMaterial = (newMaterial) => {
    setInventoryData(prev => [...prev, {
      id: newMaterial.product.id,
      name: newMaterial.product.name
    }]);
    setRawMaterialInputs(prev => [...prev, {
      label: newMaterial.product.name,
      value: newMaterial.product.id.toString(),
      product: newMaterial.product
    }]);
  };

  const handleAddOutput = async (newOutput) => {
    setPrefilledOutput(newOutput);
  };

  const handleAddInputFormula = async (newInputFormula) => {
    setinputFormula(newInputFormula);
  };

  const handleAddUtility = async (newUtility) => {
    const res = await postFetcher(['/new/workflow-app/utilities/', newUtility]);
    fetchUtilityList();
  };

  const handleAddCost = async (newCost) => {
    const res = await postFetcher(['/new/workflow-app/costs/', newCost]);
    fetchCostList();
  };

  const getConnectionMessage = () => {
    if (sourceNodeForEdge && targetNodeForEdge) {
      return `Select an output from ${sourceNodeForEdge.data?.label || 'Unknown'} to connect to ${targetNodeForEdge.data?.label || 'Unknown'}`;
    }
    if (sourceNodeForEdge) {
      return `Select an output from ${sourceNodeForEdge.data?.label || 'Unknown'} to connect to new node`;
    }
    return 'Please wait, loading node information...';
  };

  const handleCancelOutputSelection = () => {
    if (edgeCreationType === 'manual') {
      const pendingLinkId = sessionStorage.getItem('pendingLinkId');
      if (pendingLinkId) {
        setRfEdges(eds => eds.filter(e => e.id !== pendingLinkId));
      }
    }

    sessionStorage.removeItem('pendingLinkId');
    setShowOutputSelectionDialog(false);
    setSourceNodeForEdge(null);
    setTargetNodeForEdge(null);
    setSelectedOutput(null);
    setEdgeCreationType(null);
    setPrefilledOutput(0);

    if (isErrorEdgeMode) {
      setIsErrorEdgeMode(false);
      setErrorSourceNode(null);
    }
  };

  const handleConfirmOutputSelection = () => {
    if (!selectedOutput) {
      enqueueSnackbar('Please select an output for the edge', { variant: 'error' });
      return;
    }

    if (edgeCreationType !== 'error') {
      setAllocatedoutputs(prev => [...prev, selectedOutput.value]);
    }

    if (sourceNodeForEdge && targetNodeForEdge) {
      handleCreateEdgeWithOutput(sourceNodeForEdge, targetNodeForEdge, false);
    }

    sessionStorage.removeItem('pendingLinkId');
    setIsErrorEdgeMode(false);
    setErrorSourceNode(null);
    setShowOutputSelectionDialog(false);
    setSourceNodeForEdge(null);
    setTargetNodeForEdge(null);
    setSelectedOutput(null);
    setEdgeCreationType(null);
  };

  useEffect(() => {
    if (!showDialog) {
      sessionStorage.removeItem('savedOutputIds');
    }
  }, [showDialog]);

  const handleCancelErrorMode = () => {
    setIsErrorEdgeMode(false);
    setErrorSourceNode(null);
    enqueueSnackbar("Error edge mode cancelled", { variant: 'warning' });
  };

  const fetchClientData = async () => {
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_HOST_API}/order-app/list/client/`,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      setClientData(response.data);
    } catch (error) {
      console.error('Error fetching clients:', error.response?.data || error.message);
    }
  };

  useEffect(() => {
    fetchClientData();
  }, []);

  useEffect(() => {
    const preselectClient = async () => {
      if (!clientData || clientData.length === 0) return;

      // Priority 1: client_id passed in URL query parameter
      if (clientIdFromUrl) {
        const match = clientData.find(
          (c) => c.id === parseInt(clientIdFromUrl, 10) || c.id?.toString() === clientIdFromUrl.toString()
        );
        if (match) {
          setSelectedClient(match);
          return;
        }
      }

      // Priority 2: fetch client from target variant
      const targetVariantId = variantId || (finalQuantity?.product_variant_ids && finalQuantity.product_variant_ids[0]);
      if (targetVariantId) {
        try {
          const variantsList = await fetcher(['/products-app/product-variant/list/']);
          if (Array.isArray(variantsList)) {
            const targetVariant = variantsList.find(
              (v) => v.id === parseInt(targetVariantId, 10) || v.id?.toString() === targetVariantId.toString()
            );
            const vClientId = targetVariant?.client?.id || targetVariant?.client_id || (typeof targetVariant?.client === 'number' || typeof targetVariant?.client === 'string' ? targetVariant?.client : null);
            if (vClientId) {
              const match = clientData.find(
                (c) => c.id === parseInt(vClientId, 10) || c.id?.toString() === vClientId.toString()
              );
              if (match) {
                setSelectedClient(match);
                return;
              }
            }
          }
        } catch (err) {
          console.error('Error preselecting client from variant:', err);
        }
      }

      // Priority 3: fetch client from target final product
      if (finalProductId) {
        try {
          const variantsList = await fetcher(['/products-app/product-variant/list/']);
          if (Array.isArray(variantsList)) {
            const targetVariant = variantsList.find(
              (v) => (v.product_id || v.product?.id) === parseInt(finalProductId, 10)
            );
            const vClientId = targetVariant?.client?.id || targetVariant?.client_id || (typeof targetVariant?.client === 'number' || typeof targetVariant?.client === 'string' ? targetVariant?.client : null);
            if (vClientId) {
              const match = clientData.find(
                (c) => c.id === parseInt(vClientId, 10) || c.id?.toString() === vClientId.toString()
              );
              if (match) {
                setSelectedClient(match);
                return;
              }
            }
          }
        } catch (err) {
          console.error('Error preselecting client from product:', err);
        }
      }
    };

    if (!selectedClient) {
      preselectClient();
    }
  }, [clientData, clientIdFromUrl, variantId, finalProductId, finalQuantity, selectedClient]);

  // Effect to resolve final product name
  useEffect(() => {
    const resolveProductName = async () => {
      if (productNameFromUrl) {
        setResolvedProductName(productNameFromUrl);
        return;
      }

      const targetVariantId = variantId || (finalQuantity?.product_variant_ids && finalQuantity.product_variant_ids[0]);
      if (targetVariantId) {
        try {
          const variantsList = await fetcher(['/products-app/product-variant/list/']);
          if (Array.isArray(variantsList)) {
            const targetVariant = variantsList.find(
              (v) => v.id === parseInt(targetVariantId, 10) || v.id?.toString() === targetVariantId.toString()
            );
            if (targetVariant?.name) {
              setResolvedProductName(targetVariant.name);
              return;
            }
          }
        } catch (err) {
          console.error('Error resolving product name:', err);
        }
      }

      if (finalProductId) {
        try {
          const variantsList = await fetcher(['/products-app/product-variant/list/']);
          if (Array.isArray(variantsList)) {
            const targetVariant = variantsList.find(
              (v) => (v.product_id || v.product?.id) === parseInt(finalProductId, 10)
            );
            if (targetVariant?.name) {
              setResolvedProductName(targetVariant.name);
              return;
            }
          }
        } catch (err) {
          console.error('Error resolving product name from finalProductId:', err);
        }
      }
    };

    resolveProductName();
  }, [productNameFromUrl, variantId, finalProductId, finalQuantity]);

  // Pre-fill workflowName with final product name when Save Workflow Dialog opens
  useEffect(() => {
    if (showSaveDialog && resolvedProductName && (!workflowName || workflowName.trim() === '')) {
      setWorkflowName(resolvedProductName);
    }
  }, [showSaveDialog, resolvedProductName]);

  const handleSelectClient = (newValue) => {
    setSelectedClient(newValue);
  };

  // Create a mock engineRef for compatibility with components that might expect it
  const mockEngineRef = useRef({
    getModel: () => ({
      getNodes: () => rfNodes.map(node => ({
        getID: () => node.id,
        getOptions: () => node.data,
        getPorts: () => ({}),
        getOutPorts: () => [],
        getInPorts: () => [],
        getPort: () => null
      })),
      getLinks: () => rfEdges
    })
  });

  return (
    <Box sx={{ display: "flex", height: "100vh" }}>
      <Sidebar>
        <Box>
          <Typography variant="h6" gutterBottom>
            Production 2nd Step
          </Typography>
          {/* <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={() => setShowDialog(true)}
            sx={{ mb: 1 }}
          >
            + Create New Node
          </Button> */}


            <Paper
      elevation={0}
      sx={{
        mb: 2,
        p: 1,
        background: smartViewOpen ? ACCENT_LIGHT : 'transparent',
        border: `2px solid ${smartViewOpen ? ACCENT : '#e4e4e7'}`,
        borderRadius: '50px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        transition: 'all 0.2s',
        '&:hover': {
          background: smartViewOpen ? ACCENT_LIGHT : '#f4f4f5',
        }
      }}
      onClick={() => setSmartViewOpen(!smartViewOpen)}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Iconify 
          icon={smartViewOpen ? "mdi:eye" : "mdi:eye-outline"} 
          width={20}
          sx={{ color: smartViewOpen ? ACCENT : '#71717a' }}
        />
        <Typography sx={{ 
          fontWeight: 600, 
          fontSize: '13px',
          color: smartViewOpen ? ACCENT : 'text.primary'
        }}>
          Toggle to Smart View
        </Typography>
      </Box>
      <Box
        sx={{
          width: 20,
          height: 20,
          borderRadius: '50%',
          background: smartViewOpen ? ACCENT : '#e4e4e7',
          transition: 'all 0.2s',
        }}
      />
    </Paper>
          <Button
            variant="contained"
            color="secondary"
            fullWidth
            onClick={() => setShowSaveDialog(true)}
            sx={{ mb: 1 }}
            disabled={!issaveworkflowready}
          >
            Save Workflow
          </Button>
          {finalQuantity && finalQuantity.product_ids && finalQuantity.product_ids.length > 0 && (
  <Button
    variant="outlined"
    color="primary"
    fullWidth
    onClick={() => setShowVariantDialog(true)}
    sx={{ mb: 1 }}
    startIcon={<Iconify icon="mdi:cube-outline" />}
  >
    Configure Product Variants
  </Button>
)}
          <FormControl fullWidth sx={{ mb: 1 }}>
            <InputLabel>Select Workflow</InputLabel>
            <Select
              value={selectedWorkflow}
              onChange={(e) => setSelectedWorkflow(e.target.value)}
              label="Select Workflow"
            >
              <MenuItem value="" disabled>Select Workflow</MenuItem>
              {savedWorkflows?.map(workflow => (
                <MenuItem key={workflow.id} value={workflow.name}>
                  {workflow.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl fullWidth sx={{ mb: 1 }}>
            <InputLabel>Select Workflow Variants</InputLabel>
            <Select
              value={selectedworkflowvariant}
              onChange={(e) => setselectedworkflowvariant(e.target.value)}
              label="Select Workflow Variants"
            >
              <MenuItem value="" disabled>Select Workflow Variant</MenuItem>
              {variantoptions?.map(workflow => (
                <MenuItem key={workflow.id} value={workflow.name}>
                  {workflow.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            color="inherit"
            fullWidth
            onClick={() => loadWorkflow()}
            disabled={!selectedWorkflow && !selectedworkflowvariant}
            sx={{ mb: 2 }}
          >
            Load Workflow
          </Button>
          {isErrorEdgeMode && (
            <ErrorModeBox elevation={2}>
              <Typography variant="subtitle2" color="error.main" gutterBottom>
                ERROR EDGE MODE
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Source: {errorSourceNode?.data?.label}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                Click on target node
              </Typography>
              <Button
                variant="contained"
                color="error"
                fullWidth
                onClick={handleCancelErrorMode}
                sx={{ mt: 1 }}
              >
                Cancel Error Mode
              </Button>
            </ErrorModeBox>
          )}
          <Divider />
          <Box>
            {availableNodes.map((node) => (
              <NodeItem
                key={node.id}
                draggable={!isWorkflowLoaded}
                onDragStart={(e) => handleDragStart(e, node)}
                onDragEnd={handleDragEnd}
                elevation={1}
                sx={{
                  opacity: isWorkflowLoaded ? 0.5 : 1,
                  cursor: isWorkflowLoaded ? 'not-allowed' : 'grab',
                }}
              >

                <Iconify sx={{
                  width: 25,
                  height: 25,
                  flexShrink: 0,
                }} icon="fluent-mdl2:process-meta-task" width={18} />
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {node.name}
                </Typography>
              </NodeItem>
            ))}
            {availableNodes.length === 0 && (
              <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2, fontStyle: "italic" }}>
                No nodes available. Create one first!
              </Typography>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }} >
          <Button color="secondary" variant="contained" onClick={() => route.push('/production')} startIcon={<Iconify icon="mingcute:arrow-left-fill" />} sx={{ fontSize: '0.7rem' }}>1st Step </Button>
          <Button color="secondary" variant="contained" onClick={() => route.push('/production3rdstep')} endIcon={<Iconify icon="maki:arrow" />} sx={{ fontSize: '0.7rem' }}>3rd Step </Button>
        </Box>
      </Sidebar>

      <CanvasContainer>
        <Paper
          elevation={3}
          sx={{
            position: "absolute",
            top: 10,
            right: 10,
            p: 1,
            bgcolor: "rgba(255, 255, 255, 0.9)",
            zIndex: 1000,
          }}
        >
          <Typography variant="caption" color="text.secondary">
            
            {isErrorEdgeMode && (
              <Typography variant="caption" color="error.main" fontWeight="bold" display="block" sx={{ mt: 0.5 }}>
                🔴 ERROR EDGE MODE - Click target node
              </Typography>
            )}
          </Typography>
        </Paper>
        <Box
          onDragOver={handleDragOver}
          onDrop={handleDrop}
          sx={{
            height: "100%",
            border: 1,
            borderColor: "grey.300",
            bgcolor: "grey.50",
            position: "relative",
          }}
        >
          <ReactFlow
            nodes={rfNodes}
            edges={rfEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClickHandler}
            onInit={onInit}
            nodeTypes={nodeTypes}
            nodesDraggable={!isWorkflowLoaded}
            fitView
            minZoom={0.1}
            maxZoom={1.5}
            defaultViewport={{ x: 0, y: 0, zoom: 1 }}
          >
            <Background />
            <Controls />
          </ReactFlow>

          {isSavingNodes && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: "rgba(255, 255, 255, 0.8)",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                zIndex: 1000,
              }}
            >
              <CircularProgress size={60} thickness={4} sx={{ mb: 2 }} />
              <Typography variant="h6" color="primary" fontWeight="bold">
                Saving Connected Nodes...
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Please wait while we save all nonvariant connected nodes
              </Typography>
            </Box>
          )}
          {isDragging && (
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                bgcolor: "rgba(0, 123, 255, 0.1)",
                border: 2,
                borderColor: "primary.main",
                borderStyle: "dashed",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                pointerEvents: "none",
                zIndex: 100,
              }}
            >
              <Typography color="primary.main" fontWeight="bold">
                Drop here to add node
              </Typography>
            </Box>
          )}
          {/* {rfNodes.length === 0 && (
            <AddNodeButton
              onClick={() => setShowDialog(true)}
              size="large"
              aria-label="Add new node"
            >
              <Iconify icon="mdi:plus" width={24} />
              <Typography variant="caption" sx={{ ml: 1, display: { xs: 'none', sm: 'inline' } }}>
                Add Node
              </Typography>
            </AddNodeButton>
          )} */}
        </Box>
      </CanvasContainer>
<SmartView
  nodes={nodes}
  rfNodes={rfNodes}
  open={smartViewOpen}
  onClose={() => setSmartViewOpen(false)}
  inventoryOptions={inventoryOptions}
  utilityOptions={utilityOptions}
  costOptions={costOptions}
  onNodeUpdate={handleSmartViewNodeUpdate}
  allVariantIds={allVariantIds}
  setAllVariantIds={setAllVariantIds}
  setissaveworkflowready={setissaveworkflowready}
  setShowVariantDialog={setShowVariantDialog}
  finalQuantity={finalQuantity}
  issaveworkflowready={issaveworkflowready}
  setShowSaveDialog={setShowSaveDialog}
  hasWorkflowVariantId={Boolean(
    secondaryid ||
    (nodefromworkflow && (nodefromworkflow.workflow_creation_id || nodefromworkflow.id)) ||
    (selectedworkflowvariant && variantoptions && variantoptions.some((v) => v.name === selectedworkflowvariant))
  )}
  onRegisterSaveAll={(fn) => { saveAllNodesRef.current = fn; }}
/>

      <NodePropertiesDrawer2ndStep
        key={selectedNode?.data?.backendId || 'drawer'}
        open={drawerOpen}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedEdgeId(null);
          setEditEdgeLabel("");
        }}
        selectedNode={selectedNode}
        nodeData={nodeData}
        loadingNodeData={loadingNodeData}
        allVariantIds={allVariantIds}
        setAllVariantIds={setAllVariantIds}
        editNodeName={editNodeName}
        setEditNodeName={setEditNodeName}
        editNodeInputType={editNodeInputType}
        setEditNodeInputType={setEditNodeInputType}
        editRawMaterialInputs={editRawMaterialInputs}
        setEditRawMaterialInputs={setEditRawMaterialInputs}
        editPreviousNodeInputs={editPreviousNodeInputs}
        setEditPreviousNodeInputs={setEditPreviousNodeInputs}
        editUtilityInputs={editUtilityInputs}
        setEditUtilityInputs={setEditUtilityInputs}
        editNodeCost={editNodeCost}
        setEditNodeCost={setEditNodeCost}
        inventoryOptions={inventoryOptions}
        utilityOptions={utilityOptions}
        costOptions={costOptions}
        sourceNodeOptions={sourceNodeOptions}
        connectedEdges={connectedEdges}
        selectedEdgeId={selectedEdgeId}
        setSelectedEdgeId={setSelectedEdgeId}
        editEdgeLabel={editEdgeLabel}
        setEditEdgeLabel={setEditEdgeLabel}
        handleUpdateNode={handleUpdateNode}
        handleErrorEdgeButtonClick={handleErrorEdgeButtonClick}
        handleDeleteSelectedNode={handleDeleteSelectedNode}
        handleAddOrEditEdgeLabel={handleAddOrEditEdgeLabel}
        handleRemoveEdge={handleRemoveEdge}
        handleOutputDialog={handleoutputdialog}
        handleInputFormulaDialog={handleinputformuladialog}
        handleRawMaterialDialog={() => setShowRawMaterialDialog(true)}
        handleUtilityDialog={() => setShowUtilityDialog(true)}
        handleCostDialog={() => setShowCostDialog(true)}
        isErrorEdgeMode={isErrorEdgeMode}
        setselectedVariantsfromparent={setselectedVariantsfromparent}
        selectedVariantsfromparent={selectedVariantsfromparent}
        seteditoutputids={seteditoutputids}
        editoutputids={editoutputids}
        setutitilityfromdrawer={setutitilityfromdrawer}
        setissaveworkflowready={setissaveworkflowready}
        nodes={nodes}
        isEditing={isEditing}
        setIsEditing={setIsEditing}
        clickednodes={clickednodes}
        setclickednodes={setclickednodes}
        selectedworkflowId={selectedworkflowId}
        onRegularVariantsSaved={handleRegularVariantsSaved}
        onAllocableVariantsSaved={handleAllocableVariantsSaved}
      />

      <SaveWorkflowDialog
        open={showSaveDialog}
        onClose={() => setShowSaveDialog(false)}
        workflowName={workflowName}
        setWorkflowName={setWorkflowName}
        clientData={clientData}
        selectedClient={selectedClient}
        handleSelectClient={handleSelectClient}
        onSave={saveWorkflow}
        location={location}
      />

      {/* Create Node Dialog */}
      <Dialog
        open={showDialog}
        onClose={() => setShowDialog(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)'
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="h5" component="h2" fontWeight={600}>
            Create New Node Template
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Define the properties and connections for your new node
          </Typography>
        </DialogTitle>

        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
              Node Appearance
            </Typography>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Card
                sx={{
                  flex: 1,
                  cursor: 'pointer',
                  border: nodeType === 'default' ? 2 : 1,
                  borderColor: nodeType === 'default' ? 'primary.main' : 'divider'
                }}
                onClick={() => setNodeType('default')}
              >
                <CardContent>
                  <Typography variant="body2">Default (In/Out Ports)</Typography>
                </CardContent>
              </Card>

              <Card
                sx={{
                  flex: 1,
                  cursor: 'pointer',
                  border: nodeType === 'diamond' ? 2 : 1,
                  borderColor: nodeType === 'diamond' ? 'primary.main' : 'divider'
                }}
                onClick={() => setNodeType('diamond')}
              >
                <CardContent>
                  <Typography variant="body2">Diamond Node</Typography>
                </CardContent>
              </Card>
            </Box>
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
              Basic Information
            </Typography>

            <TextField
              label="Node Name"
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              fullWidth
              margin="normal"
              autoFocus
              variant="outlined"
              sx={{ mb: 2 }}
            />

            <TextField
              label="Input Name"
              value={inputname}
              onChange={(e) => setInputname(e.target.value)}
              fullWidth
              margin="normal"
              variant="outlined"
            />
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
              Input Configuration
            </Typography>

            <FormControl component="fieldset" fullWidth>
              <Typography variant="subtitle1" gutterBottom sx={{ fontWeight: 500 }}>
                Input Type
              </Typography>

              <ToggleButtonGroup
                value={nodeInputType}
                exclusive
                onChange={(e, newValue) => newValue && setNodeInputType(newValue)}
                fullWidth
                sx={{ mb: 2 }}
              >
                <ToggleButton value="raw">Raw Material</ToggleButton>
                <ToggleButton value="previous">Output from Previous Node</ToggleButton>
                <ToggleButton value="utility">Utility</ToggleButton>
              </ToggleButtonGroup>
            </FormControl>

            {nodeInputType === "raw" && (
              <Box>
                <Autocomplete
                  multiple
                  options={inventoryOptions}
                  getOptionLabel={(option) => option.label}
                  value={rawMaterialInputs}
                  isOptionEqualToValue={(option, value) => option.value === value.value}
                  onChange={(event, newValue) => setRawMaterialInputs(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Raw Material Inputs"
                      variant="outlined"
                      helperText="Select the raw materials required for this node"
                    />
                  )}
                  fullWidth
                  sx={{ mb: 2 }}
                />
              </Box>
            )}

            {nodeInputType === "previous" && (
              <Autocomplete
                options={sourceNodeOptions}
                getOptionLabel={(option) => option.label}
                value={previousNodeInputs[0] || null}
                onChange={(event, newValue) => setPreviousNodeInputs(newValue ? [newValue] : [])}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Previous Node Inputs"
                    variant="outlined"
                    helperText="Select outputs from previous nodes"
                  />
                )}
                fullWidth
                sx={{ mb: 1 }}
              />
            )}

            {nodeInputType === "utility" && (
              <Box>
                <Autocomplete
                  multiple
                  options={utilityOptions}
                  getOptionLabel={(option) => option.label}
                  value={utilityInputs}
                  onChange={(event, newValue) => setUtilityInputs(newValue)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Utility Inputs"
                      variant="outlined"
                      helperText="Select the utilities required for this node"
                    />
                  )}
                  fullWidth
                  sx={{ mb: 2 }}
                />
                <Button
                  variant="outlined"
                  onClick={() => setShowUtilityDialog(true)}
                  sx={{ mb: 1 }}
                  startIcon={<span>+</span>}
                >
                  Add New Utility
                </Button>
              </Box>
            )}
          </Box>

          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 2 }}>
              Output & Cost Configuration
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <Button
                variant="outlined"
                onClick={handleoutputdialog}
                startIcon={<span>+</span>}
              >
                Add New Output
              </Button>

              <Button
                variant="outlined"
                onClick={() => setShowCostDialog(true)}
                startIcon={<span>+</span>}
              >
                Add New Cost
              </Button>
            </Box>

            <Autocomplete
              multiple
              options={costOptions}
              getOptionLabel={(option) => option.label}
              value={nodeCost}
              onChange={(event, newValue) => setNodeCost(newValue || [])}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Costs"
                  variant="outlined"
                  helperText="Select the costs associated with this node"
                />
              )}
              fullWidth
              sx={{ mb: 2 }}
            />
          </Box>

          <Box
            sx={{
              bgcolor: 'primary.lighter',
              p: 2.5,
              borderRadius: 1.5,
              border: '1px solid',
              borderColor: 'primary.light'
            }}
          >
            <Typography variant="h6" gutterBottom sx={{ fontWeight: 500, mb: 1.5 }}>
              Configuration Summary
            </Typography>

            <Typography variant="body2" sx={{ lineHeight: 1.6 }}>
              {rawMaterialInputs.length > 0 && (
                <Box component="div" sx={{ mb: 1 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Raw Material Inputs:
                  </Box>
                  <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                    {rawMaterialInputs.map(item => item.label || item).join(", ")}
                  </Box>
                </Box>
              )}

              {previousNodeInputs.length > 0 && (
                <Box component="div" sx={{ mb: 1 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Previous Node Inputs:
                  </Box>
                  <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                    {previousNodeInputs.map(item => item.label || item).join(", ")}
                  </Box>
                </Box>
              )}

              {utilityInputs.length > 0 && (
                <Box component="div" sx={{ mb: 1 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Utility Inputs:
                  </Box>
                  <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                    {utilityInputs.map(item => item.label || item).join(", ")}
                  </Box>
                </Box>
              )}

              {inputFormula.length > 0 && (
                <Box component="div" sx={{ mb: 1 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Input formula:
                  </Box>
                  <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                    {inputFormula.map(item => item.name).join(", ")}
                  </Box>
                </Box>
              )}

              {nodeCost.length > 0 && (
                <Box component="div" sx={{ mb: 1 }}>
                  <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                    Costs:
                  </Box>
                  <Box component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                    {nodeCost.map(item => item.label || item).join(", ")}
                  </Box>
                </Box>
              )}

              {rawMaterialInputs.length === 0 &&
                previousNodeInputs.length === 0 &&
                utilityInputs.length === 0 &&
                fetchedOutputs.length === 0 && (
                  <Box sx={{
                    color: 'text.disabled',
                    fontStyle: 'italic',
                    textAlign: 'center',
                    py: 1
                  }}>
                    No inputs or outputs selected
                  </Box>
                )}
            </Typography>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          p: 3,
          pt: 2,
          borderTop: '1px solid',
          borderColor: 'divider',
          gap: 1
        }}>
          <Button
            onClick={handleCancel}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddNode}
            variant="contained"
            color="primary"
            disabled={!nodeName.trim()}
            size="large"
            sx={{ minWidth: 140 }}
          >
            Create Template
          </Button>
        </DialogActions>
      </Dialog>
<Dialog
  open={showVariantDialog}
  onClose={() => setShowVariantDialog(false)}
  maxWidth="md"
  fullWidth
>
  <DialogTitle>
    <Typography variant="h6">Select Product Variants</Typography>
    <Typography variant="body2" color="text.secondary">
      Choose variants for the final quantity products
    </Typography>
  </DialogTitle>
  <DialogContent>
    {availableVariants.length > 0 ? (
      <Autocomplete
        multiple
        options={availableVariants}
        getOptionLabel={(option) => `${option.name} (${option.product_name})`}
        value={selectedVariants}
        onChange={(event, newValue) => setSelectedVariants(newValue)}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Select Product Variants"
            variant="outlined"
            placeholder="Choose variants..."
          />
        )}
        fullWidth
      />
    ) : (
      <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 3 }}>
        No variants available for the selected products
      </Typography>
    )}
    
    {finalQuantity && (
      <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
        <Typography variant="subtitle2" gutterBottom>Current Final Quantity Configuration:</Typography>
        <Typography variant="body2">Product IDs: {finalQuantity.product_ids?.join(', ') || 'None'}</Typography>
        <Typography variant="body2">Output IDs: {finalQuantity.output_id?.join(', ') || 'None'}</Typography>
        <Typography variant="body2">Category IDs: {finalQuantity.product_category_ids?.join(', ') || 'None'}</Typography>
        <Typography variant="body2">Selected Variants: {selectedVariants.map(v => v.name).join(', ') || 'None'}</Typography>
      </Box>
    )}
  </DialogContent>
  <DialogActions>
    <Button onClick={() => setShowVariantDialog(false)} variant="outlined">
      Cancel
    </Button>
    <Button 
      onClick={handleSaveFinalQuantity} 
      variant="contained" 
      color="primary"
      disabled={selectedVariants.length === 0}
    >
      Save Variants
    </Button>
  </DialogActions>
</Dialog>
      {/* <AddRawMaterialDialog
        open={showRawMaterialDialog}
        onClose={() => setShowRawMaterialDialog(false)}
        onSave={handleAddRawMaterial}
      />

      <AddOutputDialog
        open={showOutputDialog}
        onClose={() => setShowOutputDialog(false)}
        onSave={handleAddOutput}
        rawMaterialInputs={rawMaterialInputs}
        previousNodeInputs={previousNodeInputs}
        showOutputDialog={showOutputDialog}
        allocatedoutputs={allocatedoutputs}
        engineRef={mockEngineRef}
      />

      <AddNewInputFormulaDialog
        open={showInputDialog}
        onClose={() => setshowInputDialog(false)}
        onSave={handleAddInputFormula}
        rawMaterialInputs={rawMaterialInputs}
        showDialog={showDialog}
      />

      <AddUtilityDialog
        open={showUtilityDialog}
        onClose={() => setShowUtilityDialog(false)}
        onSave={handleAddUtility}
      />

      <AddCostDialog
        open={showCostDialog}
        onClose={() => setShowCostDialog(false)}
        onSave={handleAddCost}
      />

      <Dialog
        open={showOutputSelectionDialog}
        onClose={handleCancelOutputSelection}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Select Output for Edge</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {getConnectionMessage()}
          </Typography>
          <Autocomplete
            options={edgeOutputs.filter(output => !allocatedoutputs.includes(output.value))}
            getOptionLabel={(option) => option.label || 'Unnamed Output'}
            value={selectedOutput}
            disabled={!!prefilledOutput}
            onChange={(event, newValue) => setSelectedOutput(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Select Output"
                variant="outlined"
                helperText={edgeOutputs.filter(output => !allocatedoutputs.includes(output.value)).length === 0
                  ? "No outputs available for this node"
                  : "Choose an output for the edge"}
              />
            )}
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelOutputSelection} variant="outlined">
            Cancel
          </Button>
          <Button
            onClick={handleConfirmOutputSelection}
            variant="contained"
            color="primary"
            disabled={!selectedOutput || !sourceNodeForEdge}
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog> */}
    </Box>
  );
}

export default function FactoryLayout2ndStep() {
  return (
    <ReactFlowProvider>
      <FactoryLayout2ndStepContent />
    </ReactFlowProvider>
  );
}